import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import useTicketSocket from '../hooks/useTicketSocket';
import { X, Download, Upload, Car, Settings as SettingsIcon, FileText, FileCog, Clock, User, Loader2, CheckCircle2, MessageSquare, Send, Paperclip, Image as ImageIcon, Pencil, Trash2, Check, ArrowLeft, CheckCheck, Eye, Sparkles, Languages, Bot, Tag, Factory, Cog, Cpu, Wrench, Gauge, Calendar, Hash, BookOpen, Phone, Mail, Building2, Database, Wallet, Globe, Lock, MoreVertical, Wand2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import PdfPreview from './PdfPreview';
import CustomerProfileDrawer from './CustomerProfileDrawer';
import LegacyTicketDrawer from './LegacyTicketDrawer';
import AttachmentCard from './chat/AttachmentCard';
import { PriorityCountdown } from './orders/PriorityCountdown';
import { OPTION_ICONS } from '../utils/optionIcons';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from './ui/dropdown-menu';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_LABEL = { highest: 'Highest', high: 'High', medium: 'Medium', low: 'Low', lowest: 'Lowest' };
const STATUS_LABEL = { pending: 'Pending', open: 'Open', working: 'Working', waiting: 'Waiting', completed: 'Completed', closed: 'Closed', cancelled: 'Cancelled' };
import AuthedAvatarImage from './AuthedAvatarImage';
import { CustomerBadges, useNewCustomerDays } from './CustomerBadges';

const fmt = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const formatBytes = (b) => {
  if (!b) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
};

// Initials fallback for chat avatars when no avatar_url is set yet
// (Phase C will add real avatar uploads — this stays as the
// universal fallback even after avatars ship).
const getInitials = (name, email) => {
  const src = (name || email || '').trim();
  if (!src) return '?';
  const parts = src.split(/[\s@.]+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// "5 May" / "Today" / "Yesterday" label used by chat date dividers.
// `tt` is the component-scoped translator (admin-invariant aware); when
// omitted (legacy callers, tests) we fall back to English so nothing
// crashes.
const dateLabel = (iso, tt) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
  const day = new Date(d); day.setHours(0, 0, 0, 0);
  const _ = tt || ((_k, en) => en);
  if (day.getTime() === today.getTime())     return _('drawer.today', 'Today');
  if (day.getTime() === yesterday.getTime()) return _('drawer.yesterday', 'Yesterday');
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: d.getFullYear() === today.getFullYear() ? undefined : 'numeric' });
};

// File-extension → emoji-free icon class (Lucide File icon paints all
// types uniformly; we color the badge below by category).
// Kept here for backwards-compat callers only — the shared
// `chat/AttachmentCard` component now owns extension classification.


/**
 * LegacyFileDownloadButton — admin OR customer button that streams a
 * migrated legacy binary from the new server's local disk. The
 * `/api/orders/{id}/legacy-file/{kind}` endpoint now serves files
 * directly from `uploads/orders/{id}/<basename>` symlinks created by
 * the prod migration script — no more upstream calls to the old
 * `fs.auto-remap.com` API.
 *
 * `kind` ∈ {'original','modified'} → maps server-side to
 * file_default / file_modified.
 */
function LegacyFileDownloadButton({ token, orderId, kind, label, hintPath, testid, variant }) {
  const [downloading, setDownloading] = React.useState(false);
  const onClick = React.useCallback(async (e) => {
    e.stopPropagation();
    if (!orderId) return;
    setDownloading(true);
    try {
      const r = await fetch(
        `${API_URL}/api/orders/${encodeURIComponent(orderId)}/legacy-file/${kind}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        const detail = (typeof j?.detail === 'object' ? j.detail : null) || {};
        const code = detail.code;
        const msg = detail.message || j?.detail || `HTTP ${r.status}`;
        if (code === 'legacy_file_missing' || code === 'no_file_recorded' || code === 'legacy_file_not_migrated' || r.status === 404) {
          // Surface the backend's actionable hint (e.g. "Re-run the
          // file migration script for this order") instead of the
          // older generic "No original file" — without this, admins
          // had to dig through server logs to know what to do.
          toast.error(detail.message
            || (kind === 'original' ? 'No original file' : 'No modified file'),
            { duration: 9000 });
        } else if (code === 'not_configured') {
          toast.error('File service is not configured. Please contact support.');
        } else if (code === 'forbidden' || r.status === 403) {
          toast.error('This order does not belong to your account.');
        } else if (code === 'not_legacy') {
          toast.error(`No ${kind} file available.`);
        } else {
          toast.error(`Download failed: ${msg}`);
        }
        return;
      }
      const blob = await r.blob();
      // Defensive: refuse text/HTML responses so the browser never
      // offers an .htm save — the legacy upstream sometimes leaks
      // its replacement SPA index.html through. Backend already
      // rejects this (see services/legacy_csv_api.download_file)
      // but a stale CDN cache could still serve it briefly.
      const ct = (r.headers.get('content-type') || '').toLowerCase();
      if (ct.startsWith('text/') || ct === 'application/json' || ct === 'application/xml') {
        toast.error('Legacy file is temporarily unavailable. Please retry in a moment.');
        return;
      }
      const cd = r.headers.get('content-disposition') || '';
      const mm = cd.match(/filename\*=UTF-8''([^;]+)/i) || cd.match(/filename="?([^";]+)"?/i);
      const fallback = (hintPath || '').split('/').pop() || `${kind}-${orderId}`;
      const name = mm ? decodeURIComponent(mm[1]) : fallback;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = name;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(`Downloaded ${name}`);
    } catch (err) {
      toast.error(`Download failed: ${err?.message || err}`);
    } finally {
      setDownloading(false);
    }
  }, [token, orderId, kind, hintPath]);

  const baseCls = 'w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition shadow-sm disabled:opacity-50';
  const styleCls = variant === 'modified'
    ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 text-white hover:shadow-md'
    : 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:shadow-md';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={downloading}
      data-testid={testid || `legacy-download-${kind}-${orderId}`}
      title={hintPath ? `Legacy stored path: ${hintPath}` : 'Served from new server local storage.'}
      className={`${baseCls} ${styleCls}`}
    >
      {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
      {downloading ? 'Downloading…' : label}
    </button>
  );
}

function ResponseEstimateBanner({ isAdmin, token }) {
  const { t } = useTranslation('orders');
  const [text, setText] = useState('');
  const [stats, setStats] = useState(null);
  // Live floor derived from the priorities that are actually open right
  // now (schedule, manual toggle and Flight Mode all feed into it).
  const [estimate, setEstimate] = useState(null);

  useEffect(() => {
    if (isAdmin) return;
    let stop = false;
    const load = async () => {
      try {
        const [tr, sr, pr] = await Promise.all([
          fetch(`${API_URL}/api/config/texts`),
          fetch(`${API_URL}/api/config/ticket-response-stats`),
          fetch(`${API_URL}/api/priorities/response-estimate`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          }),
        ]);
        if (stop) return;
        if (tr.ok) {
          const td = await tr.json();
          setText((td && td.ticket_response_estimate) || '');
        }
        if (sr.ok) {
          setStats(await sr.json());
        }
        if (pr.ok) {
          setEstimate(await pr.json());
        }
      } catch (_) { /* silent */ }
    };
    load();
    // Refresh every 60s while drawer is open so "online" status stays fresh.
    const id = setInterval(load, 60000);
    return () => { stop = true; clearInterval(id); };
  }, [isAdmin, token]);

  if (isAdmin) return null;

  // Build a smart, dynamic ETA line based on real activity & medians.
  let smart = null;
  if (stats) {
    const med = stats.median_response_minutes;
    const fmtDur = (mins) => {
      if (mins == null) return null;
      if (mins < 1.5) return '~1 min';
      if (mins < 60) return `~${Math.round(mins)} min`;
      const h = mins / 60;
      if (h < 10) return `~${h.toFixed(1)} h`;
      return `~${Math.round(h)} h`;
    };
    const medStr = fmtDur(med);

    if (stats.admin_online) {
      smart = {
        emoji: '🟢',
        tone: 'emerald',
        title: t('chat.engineer_online', { defaultValue: 'Engineer is online right now' }),
        sub: medStr
          ? t('chat.recent_replies', { duration: medStr, defaultValue: `Recent replies took ${medStr} on average.` })
          : t('chat.replies_minutes', { defaultValue: 'Replies usually arrive within minutes.' }),
      };
    } else if (stats.last_admin_seen_at) {
      const lastSeen = new Date(stats.last_admin_seen_at);
      const ago = Math.max(1, Math.round((Date.now() - lastSeen.getTime()) / 60000));
      const agoStr = ago < 60 ? `${ago} min` : `${Math.round(ago / 60)} h`;
      smart = {
        emoji: '🟡',
        tone: 'amber',
        title: t('chat.engineer_away', { ago: agoStr, defaultValue: `Engineer is currently away (last seen ${agoStr} ago)` }),
        sub: medStr
          ? t('chat.typical_reply', { duration: medStr, defaultValue: `Typical reply time on this team: ${medStr}.` })
          : t('chat.email_notify', { defaultValue: 'You will be notified by email when a reply arrives.' }),
      };
    } else {
      smart = {
        emoji: '⚪',
        tone: 'slate',
        title: t('chat.engineer_unknown', { defaultValue: 'Engineer activity unknown' }),
        sub: medStr ? t('chat.typical_reply_short', { duration: medStr, defaultValue: `Typical reply time: ${medStr}.` }) : null,
      };
    }
  }

  const showSmart = !!smart;

  // "Estimated reply: from 90 minutes" — recomputed from the priorities
  // that are open right now, so switching Highest/High off (or running
  // Flight Mode) immediately moves the promise the customer reads.
  const fmtFloor = (mins) => {
    if (mins == null) return null;
    // Spec wording: a 90-minute floor must read "90 minutes", not
    // "1.5 hours" — only switch to hours from 2 h upwards.
    if (mins < 120) return t('chat.floor_minutes', { count: mins, defaultValue: `${mins} minutes` });
    const h = mins / 60;
    const hStr = Number.isInteger(h) ? String(h) : h.toFixed(1);
    return t('chat.floor_hours', { count: hStr, defaultValue: `${hStr} hours` });
  };
  const estimateLine = (estimate && estimate.any_available && estimate.floor_minutes > 0)
    ? t('chat.estimated_reply_from', {
        duration: fmtFloor(estimate.floor_minutes),
        defaultValue: `Estimated reply: from ${fmtFloor(estimate.floor_minutes)} and up.`,
      })
    : (estimate && !estimate.any_available)
      ? t('chat.estimated_reply_next_day', {
          defaultValue: 'Estimated reply: your request is queued for the next working window.',
        })
      : null;

  // The admin-editable notice may still carry a hand-written ETA
  // sentence ("Estimated reply: 0.5–2 hours"). Now that the number is
  // computed from the live priorities, that sentence would contradict
  // it — so when we have a computed estimate we drop just those blocks
  // and keep the rest of the notice (working-hours / rollover note).
  const cleanedText = (() => {
    if (!text) return '';
    if (!estimateLine) return text;
    try {
      const doc = new DOMParser().parseFromString(`<div>${text}</div>`, 'text/html');
      const root = doc.body.firstChild;
      const re = /(estimated\s+repl|estimated\s+response\s+time|ожидаем\w*\s+ответ|время\s+ответа|voraussichtliche\s+antwort|r[ée]ponse\s+estim)/i;
      root.querySelectorAll('p, li, div').forEach((el) => {
        if (!el.querySelector('p, li, div') && re.test(el.textContent || '')) el.remove();
      });
      const out = root.innerHTML.trim();
      return re.test(root.textContent || '') ? '' : out;
    } catch (_) {
      return text;
    }
  })();
  const showText = !!cleanedText;

  if (!showSmart && !showText && !estimateLine) return null;
  const TONE = {
    emerald: 'from-emerald-50 to-green-50 border-emerald-200 text-emerald-900',
    amber:   'from-amber-50 to-yellow-50 border-amber-200 text-amber-900',
    slate:   'from-slate-50 to-white border-slate-200 text-slate-700',
  };
  const toneClass = smart ? TONE[smart.tone] : TONE.amber;

  return (
    <div
      data-testid="ticket-response-estimate"
      className={`px-4 py-2.5 bg-gradient-to-r ${toneClass} border-b text-[12px] leading-snug`}
    >
      {showSmart && (
        <div
          className="flex items-start gap-2"
          data-testid="ticket-response-smart"
          data-admin-online={String(stats?.admin_online ?? 'unknown')}
        >
          <span className="flex-shrink-0">{smart.emoji}</span>
          <div className="min-w-0">
            <p className="font-semibold">{smart.title}</p>
            {/* Hide the "replies took ~1 min" boast while the fast tiers
                are closed — it would contradict the floor right below. */}
            {smart.sub && !(estimate?.blocked_count > 0) && <p className="opacity-80">{smart.sub}</p>}
          </div>
        </div>
      )}
      {estimateLine && (
        <p
          className={`${showSmart ? 'mt-2 pt-2 border-t border-current/20' : ''} font-semibold`}
          data-testid="ticket-response-estimate-line"
          data-floor-minutes={estimate?.floor_minutes ?? ''}
        >
          {estimateLine}
          {estimate?.blocked_count > 0 && (
            <span className="font-normal opacity-75">
              {' '}
              {t('chat.slower_than_usual', { defaultValue: '(express slots are closed at the moment)' })}
            </span>
          )}
        </p>
      )}
      {showText && (
        <SafeHtml
          as="div"
          className={`${(showSmart || estimateLine) ? 'mt-2 pt-2 border-t border-current/20' : ''} ticket-rich-text`}
          // Admin Rich-Text-Editor output, sanitised by SafeHtml/DOMPurify.
          html={cleanedText}
        />
      )}
      <style>{`
        .ticket-rich-text p { margin: 0; }
        .ticket-rich-text p + p { margin-top: 4px; }
        .ticket-rich-text ul, .ticket-rich-text ol { margin: 4px 0; padding-left: 20px; }
        .ticket-rich-text a { text-decoration: underline; }
      `}</style>
    </div>
  );
}

const Field = ({ label, value, icon: Icon }) => (
  <div className="min-w-0">
    <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1 flex items-center gap-1">
      {Icon && <Icon className="w-3 h-3" />}
      {label}
    </p>
    <p className="text-sm text-slate-800 font-semibold truncate">{value || '—'}</p>
  </div>
);

// Compact label-value row used inside the Variant-D combined header card.
// Hides itself when there's nothing to show so the grid stays dense.
const KV = ({ label, value, icon: Icon }) => {
  if (value === null || value === undefined || value === '' || value === '—') return null;
  return (
    <div className="flex items-baseline gap-2 min-w-0">
      <span className="flex items-center gap-1 text-slate-500 w-28 flex-shrink-0 text-[11px] uppercase tracking-wider font-medium">
        {Icon && <Icon className="w-3 h-3 text-slate-400" />}
        {label}
      </span>
      <span className="font-semibold text-slate-800 truncate">{value}</span>
    </div>
  );
};

// VField — single label/value cell used inside the redesigned Vehicle
// Information card (Phase 2). Stacks label on top, value below, so the
// values become the visual focus instead of being squeezed next to a
// fixed-width label column. Used in a 2-column responsive grid with
// logical groupings (identity → engine → transmission → file).
const VField = ({ label, value, mono = false, emphasize = false }) => {
  if (value === null || value === undefined || value === '' || value === '—') return null;
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold leading-none mb-1">{label}</div>
      <div
        className={`text-sm font-semibold text-slate-800 leading-snug break-words ${
          mono ? 'font-mono text-[13px]' : ''
        } ${emphasize ? 'text-[15px] text-slate-900' : ''}`}
      >
        {value}
      </div>
    </div>
  );
};

// Quick-link buttons for popular messengers tied to a phone number.
// Shared component used by OrderDetailDrawer + CustomerProfileDrawer.
import MessengerLinks from './MessengerLinks';

import SafeHtml from './SafeHtml';
import InstructionsBlock from './InstructionsBlock';
import { triggerUnreadRefresh } from '../lib/unreadBus';
import DateDivider from './chat/DateDivider';
import { formatBubbleTime, shouldShowAuthorLabel, deriveReadStatus } from './chat/chatUtils';
import { CHAT_TOKENS } from './chat/chatTokens';
// Tiny option-icon used inside the Services list. Uses the shared
// optionIcons mapping so the same pictograms render in New Order, Order
// list and Order Detail.
const ServiceIcon = ({ optionKey, fallback }) => {
  const file = optionKey ? OPTION_ICONS[optionKey] : null;
  if (file) {
    return (
      <span className="w-6 h-6 rounded-full bg-cyan-50 border border-cyan-200 flex items-center justify-center overflow-hidden flex-shrink-0">
        <img src={`/icons/options/${file}`} alt="" className="w-4 h-4 object-contain" loading="lazy" />
      </span>
    );
  }
  // Letter-fallback so unmapped keys still get a coloured pill.
  const letters = (fallback || '?').replace(/[^A-Z0-9]+/gi, '').slice(0, 3).toUpperCase();
  return (
    <span className="w-6 h-6 rounded-full bg-cyan-100 text-cyan-700 flex items-center justify-center text-[9px] font-bold flex-shrink-0">{letters || '·'}</span>
  );
};

const STATUS_STYLE = {
  pending:   { bg: 'from-slate-400 to-slate-500',     dot: 'bg-slate-300',   text: 'text-slate-600',   chip: 'bg-slate-100' },
  open:      { bg: 'from-blue-500 to-blue-600',       dot: 'bg-blue-500',    text: 'text-blue-700',    chip: 'bg-blue-50' },
  working:   { bg: 'from-amber-500 to-orange-500',    dot: 'bg-amber-500',   text: 'text-amber-700',   chip: 'bg-amber-50' },
  waiting:   { bg: 'from-yellow-500 to-yellow-600',   dot: 'bg-yellow-500',  text: 'text-yellow-700',  chip: 'bg-yellow-50' },
  completed: { bg: 'from-emerald-500 to-emerald-600', dot: 'bg-emerald-500', text: 'text-emerald-700', chip: 'bg-emerald-50' },
  closed:    { bg: 'from-slate-500 to-slate-700',     dot: 'bg-slate-500',   text: 'text-slate-700',   chip: 'bg-slate-100' },
  cancelled: { bg: 'from-red-500 to-red-600',         dot: 'bg-red-500',     text: 'text-red-700',     chip: 'bg-red-50' },
};
const PRIORITY_STYLE = {
  highest: { bg: 'from-red-500 to-red-600',         icon: '🔥', dot: 'bg-red-500',     text: 'text-red-700',     chip: 'bg-red-50' },
  high:    { bg: 'from-orange-500 to-orange-600',   icon: '⚡', dot: 'bg-orange-500',  text: 'text-orange-700',  chip: 'bg-orange-50' },
  medium:  { bg: 'from-blue-500 to-blue-600',       icon: '◆', dot: 'bg-amber-500',   text: 'text-amber-700',   chip: 'bg-amber-50' },
  low:     { bg: 'from-emerald-500 to-emerald-600', icon: '○', dot: 'bg-emerald-500', text: 'text-emerald-700', chip: 'bg-emerald-50' },
  lowest:  { bg: 'from-slate-400 to-slate-500',     icon: '·', dot: 'bg-slate-400',   text: 'text-slate-500',   chip: 'bg-slate-100' },
};

// Convert ISO datetime to local <input type="datetime-local"> value (yyyy-MM-ddTHH:mm)
const isoToLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

function ReadyAtRow({ order, token, isAdmin, onChanged, compact = false }) {
  const { t } = useTranslation('orders');
  const tt = (key, en) => (isAdmin ? en : t(key, { defaultValue: en }));
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(isoToLocalInput(order.ready_at));
  const [saving, setSaving] = useState(false);

  useEffect(() => { setValue(isoToLocalInput(order.ready_at)); }, [order.ready_at]);

  // ── Preset helpers (admin-only) ───────────────────────────────
  // "40 min" / "90 min" quick buttons set ready_at = created_at +
  // N minutes. Saves the admin from opening the native datetime
  // picker for the two most common delivery windows.
  const applyPreset = async (minutes) => {
    if (!order.created_at) return;
    const created = new Date(order.created_at);
    if (Number.isNaN(created.getTime())) return;
    const target = new Date(created.getTime() + minutes * 60_000);
    const iso = target.toISOString();
    setValue(isoToLocalInput(iso));
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/orders/${order.id}/ready-at`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ready_at: iso }),
      });
      if (!r.ok) {
        const txt = await r.text();
        alert('Save failed: ' + txt);
        return;
      }
      setEditing(false);
      if (onChanged) onChanged();
    } finally {
      setSaving(false);
    }
  };

  const save = async (clear = false) => {
    setSaving(true);
    try {
      const body = { ready_at: clear ? null : (value ? new Date(value).toISOString() : null) };
      const r = await fetch(`${API_URL}/api/orders/${order.id}/ready-at`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const t = await r.text();
        alert('Save failed: ' + t);
        return;
      }
      setEditing(false);
      if (onChanged) onChanged();
    } finally {
      setSaving(false);
    }
  };

  if (!isAdmin) {
    if (!order.ready_at) return null;
    if (compact) {
      return (
        <span className="inline-flex items-center gap-1.5" data-testid="ready-at-display">
          <span className="w-1.5 h-1.5 rounded-full bg-cyan-500" />
          <span className="text-slate-400 uppercase tracking-wider text-[10px] font-bold">{tt('drawer.ready_at', 'Ready at')}</span>
          <span className="font-semibold text-cyan-700">{fmt(order.ready_at)}</span>
        </span>
      );
    }
    return (
      <div className="flex items-center gap-3" data-testid="ready-at-display">
        <span className="w-2 h-2 rounded-full bg-cyan-500" />
        <span className="text-slate-500 flex-1">{tt('drawer.will_be_ready_at', 'Will be ready at')}</span>
        <span className="font-semibold text-cyan-700">{fmt(order.ready_at)}</span>
      </div>
    );
  }

  if (compact) {
    return (
      <span className="inline-flex items-center gap-1.5 flex-wrap" data-testid="ready-at-row">
        <span className="w-1.5 h-1.5 rounded-full bg-cyan-500" />
        <span className="text-slate-400 uppercase tracking-wider text-[10px] font-bold">Ready at</span>
        {editing ? (
          <span className="inline-flex items-center gap-1">
            <input
              type="datetime-local"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className="px-1.5 py-0.5 border border-slate-300 rounded text-[11px]"
              data-testid="ready-at-input"
            />
            <button
              onClick={() => save(false)}
              disabled={saving || !value}
              className="p-1 bg-emerald-500 hover:bg-emerald-600 text-white rounded disabled:opacity-40"
              title="Save"
              data-testid="ready-at-save"
            >
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
            </button>
            {order.ready_at && (
              <button
                onClick={() => save(true)}
                disabled={saving}
                className="p-1 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded"
                title="Clear (revert to priority deadline)"
                data-testid="ready-at-clear"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            )}
            <button
              onClick={() => { setEditing(false); setValue(isoToLocalInput(order.ready_at)); }}
              className="p-1 hover:bg-slate-200 text-slate-500 rounded"
              title="Cancel"
            >
              <X className="w-3 h-3" />
            </button>
            {/* Quick presets — set ready_at = created_at + N minutes.
                Skip these if the order has no created_at (defensive). */}
            {order.created_at && (
              <>
                <span className="w-px h-4 bg-slate-300 mx-1" />
                <button
                  type="button"
                  onClick={() => applyPreset(40)}
                  disabled={saving}
                  className="px-2 py-0.5 rounded bg-cyan-100 hover:bg-cyan-200 text-cyan-800 text-[10px] font-bold disabled:opacity-40"
                  title="Set ready-at to created_at + 40 minutes"
                  data-testid="ready-at-preset-40"
                >
                  +40 min
                </button>
                <button
                  type="button"
                  onClick={() => applyPreset(90)}
                  disabled={saving}
                  className="px-2 py-0.5 rounded bg-cyan-100 hover:bg-cyan-200 text-cyan-800 text-[10px] font-bold disabled:opacity-40"
                  title="Set ready-at to created_at + 90 minutes"
                  data-testid="ready-at-preset-90"
                >
                  +90 min
                </button>
              </>
            )}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 flex-wrap">
            <button
              onClick={() => setEditing(true)}
              className="font-semibold text-cyan-700 hover:underline text-[11px]"
              data-testid="ready-at-edit"
            >
              {order.ready_at ? fmt(order.ready_at) : 'set custom time'}
            </button>
            {/* Presets visible in COLLAPSED state so admin can set OR
                override ready-at in one click without opening the picker. */}
            {order.created_at && (
              <>
                <button
                  type="button"
                  onClick={() => applyPreset(40)}
                  disabled={saving}
                  className="px-2 py-0.5 rounded bg-cyan-100 hover:bg-cyan-200 text-cyan-800 text-[10px] font-bold disabled:opacity-40"
                  title="Set ready-at to created_at + 40 minutes"
                  data-testid="ready-at-preset-40"
                >
                  +40 min
                </button>
                <button
                  type="button"
                  onClick={() => applyPreset(90)}
                  disabled={saving}
                  className="px-2 py-0.5 rounded bg-cyan-100 hover:bg-cyan-200 text-cyan-800 text-[10px] font-bold disabled:opacity-40"
                  title="Set ready-at to created_at + 90 minutes"
                  data-testid="ready-at-preset-90"
                >
                  +90 min
                </button>
              </>
            )}
          </span>
        )}
      </span>
    );
  }

  return (
    <div className="flex items-center gap-3" data-testid="ready-at-row">
      <span className="w-2 h-2 rounded-full bg-cyan-500" />
      <span className="text-slate-500 flex-1">Will be ready at</span>
      {editing ? (
        <div className="flex items-center gap-1">
          <input
            type="datetime-local"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="px-2 py-1 border border-slate-300 rounded text-xs"
            data-testid="ready-at-input"
          />
          <button
            onClick={() => save(false)}
            disabled={saving || !value}
            className="p-1.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded disabled:opacity-40"
            title="Save"
            data-testid="ready-at-save"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
          </button>
          {order.ready_at && (
            <button
              onClick={() => save(true)}
              disabled={saving}
              className="p-1.5 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded"
              title="Clear (revert to priority deadline)"
              data-testid="ready-at-clear"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
          <button
            onClick={() => { setEditing(false); setValue(isoToLocalInput(order.ready_at)); }}
            className="p-1.5 hover:bg-slate-200 text-slate-500 rounded"
            title="Cancel"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        <button
          onClick={() => setEditing(true)}
          className="font-semibold text-cyan-700 hover:underline text-sm"
          data-testid="ready-at-edit"
        >
          {order.ready_at ? fmt(order.ready_at) : 'set custom time'}
        </button>
      )}
    </div>
  );
}

export default function OrderDetailDrawer({ order: orderProp, token, isAdmin, onClose, onChanged, onEditOrder }) {
  const newCustomerDays = useNewCustomerDays(isAdmin ? token : null);
  // i18n: customers see translated labels; admins keep English via `tt`.
  const { t } = useTranslation('orders');
  const tt = (key, en) => (isAdmin ? en : t(key, { defaultValue: en }));
  // If the parent only knows the order id (e.g. TicketsList), fetch the
  // full order record here so the drawer has order_number, files, etc.
  const [fetchedOrder, setFetchedOrder] = useState(null);
  useEffect(() => {
    // Re-fetch only when the *id* of the incoming order changes — the
    // parent often re-creates `orderProp` on every render (inline object
    // literal), so adding the whole prop to deps would re-fire this
    // effect on every parent render and hammer the API.
    if (!orderProp?.id) return;
    // Consider "full" when more than just id is provided.
    if (Object.keys(orderProp).length > 1) { setFetchedOrder(null); return; }
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/orders/${orderProp.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.ok) setFetchedOrder(await r.json());
      } catch (_) { /* silent */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderProp?.id, token]);

  const order = fetchedOrder || orderProp;
  // Lightweight customer enrichment for the Customer card on admin
  // order detail view — balance / country / last login IP. We only
  // fetch it when admin is viewing an order with a known user_id,
  // and only once per drawer open.
  const [customerInfo, setCustomerInfo] = useState(null);
  useEffect(() => {
    if (!isAdmin || !order?.user_id) { setCustomerInfo(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const [pRes, lRes] = await Promise.all([
          fetch(`${API_URL}/api/admin/customers/${order.user_id}/profile`, { headers: { Authorization: `Bearer ${token}` } }),
          // Most recent login event = last known IP. limit=1 keeps it cheap.
          fetch(`${API_URL}/api/admin/login-history?user_id=${order.user_id}&only_success=true&limit=1`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        const profile = pRes.ok ? await pRes.json() : null;
        const login   = lRes.ok ? await lRes.json() : null;
        const lastEv  = login?.items?.[0] || null;
        if (cancelled) return;
        setCustomerInfo({
          balance:        profile?.balance ?? null,
          reserved:       profile?.reserved ?? null,
          country:        profile?.country || null,
          email:          profile?.email   || null,
          is_company:        !!profile?.is_company,
          company_name:      profile?.company_name || null,
          registration_date: profile?.registration_date || profile?.created_at || null,
          last_login_ip:  lastEv?.ip       || null,
          last_login_at:  lastEv?.created_at || null,
        });
      } catch (_) { /* silent */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, order?.user_id, token]);

  const [uploading, setUploading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const [internalNote, setInternalNote] = useState(false);  // admin-only toggle
  const [lightbox, setLightbox] = useState(null); // { url, name }
  const { currentUser } = useAuth();
  const currentUserId = currentUser?.id || null;
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState('');
  // Admin-only: clicking a customer name anywhere in the drawer opens the
  // full profile drawer. `null` = closed; any string = customer user_id.
  const [openCustomerId, setOpenCustomerId] = useState(null);
  // Legacy conversation (Cycle B refinement): we fetch the imported PHP
  // messages and render them INLINE in the regular chat panel using the
  // same bubble template. legacyMessages is a list with the same shape
  // as `messages` plus `is_legacy=true` so the render loop can skip
  // edit/delete actions on historical rows.
  const [legacyTicket, setLegacyTicket] = useState(null);
  const [legacyMessages, setLegacyMessages] = useState([]);
  const messagesEndRef = useRef(null);

  // 1-year warranty/support lock — customers cannot post new messages on
  // orders older than 365 days from `order.created_at` (legacy orders use
  // the imported creation date which gets stored on the same field at
  // import time). Admins are NEVER locked. The message template is loaded
  // from /api/config/texts (admin-editable, see Settings → Config texts).
  const [warrantyMsgTpl, setWarrantyMsgTpl] = useState('');
  useEffect(() => {
    if (isAdmin) return; // admins can always post — skip the fetch.
    let stop = false;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/config/texts`);
        if (!r.ok || stop) return;
        const td = await r.json();
        setWarrantyMsgTpl((td && td.support_ticket_time_message) || '');
      } catch (_) { /* silent */ }
    })();
    return () => { stop = true; };
  }, [isAdmin]);

  // Compute warranty state from the order's creation date. 365-day window
  // — same threshold for current + legacy tickets (per product spec).
  const warrantyInfo = (() => {
    if (isAdmin) return { expired: false, createdLabel: '' };
    const createdIso = order?.created_at || order?.legacy_created_at || null;
    if (!createdIso) return { expired: false, createdLabel: '' };
    const created = new Date(createdIso);
    if (Number.isNaN(created.getTime())) return { expired: false, createdLabel: '' };
    const ageMs = Date.now() - created.getTime();
    const oneYearMs = 365 * 24 * 60 * 60 * 1000;
    return { expired: ageMs > oneYearMs, createdLabel: fmt(createdIso) };
  })();
  // Substitute {created_date} placeholder. Quill stores rich HTML so we
  // do a simple string replace which works inside any wrapping tag.
  const warrantyMsgHtml = warrantyInfo.expired
    ? (warrantyMsgTpl || '').replace(/\{created_date\}/g, warrantyInfo.createdLabel)
    : '';

  // PDF preview modal
  const [pdfPreview, setPdfPreview] = useState(null); // ticket message obj

  // Customer-note attachment preview (image or PDF). When set, an overlay
  // shows the file inline. Format: { kind: 'image'|'pdf', blobUrl, name }
  const [notePreview, setNotePreview] = useState(null);
  // Admin: dropdown for destructive actions (Cancel & Refund / Delete).
  // Closed by default — keeps the header free of always-visible danger
  // buttons that can be clicked by accident. Closed on outside-click +
  // when the action runs.
  const [actionsMenu, setActionsMenu] = useState(false);
  // The description attachment accepts ANY file type (customers routinely
  // attach .bin / .zip / .ori stock reads there, not just screenshots).
  // Images and PDFs get the inline preview overlay; everything else must be
  // downloadable — previously the chip only ever opened a preview, so a
  // .bin attachment was visible in admin but impossible to retrieve.
  const PREVIEWABLE_IMAGE_EXT = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'avif', 'heic', 'heif'];
  const noteAttachment = useMemo(() => {
    const name = order?.customer_note_photo_name;
    if (!name) return null;
    const ext = (name.split('.').pop() || '').toLowerCase();
    const kind = ext === 'pdf' ? 'pdf' : (PREVIEWABLE_IMAGE_EXT.includes(ext) ? 'image' : 'file');
    return { name, ext, kind };
  }, [order?.customer_note_photo_name]);  // eslint-disable-line react-hooks/exhaustive-deps

  const openNotePreview = useCallback(async () => {
    if (!order?.id || !noteAttachment) return;
    const { kind, name } = noteAttachment;
    try {
      const r = await fetch(`${API_URL}/api/orders/${order.id}/file/note-photo`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const raw = await r.blob();
      if (kind === 'file') {
        // Not previewable → stream it to disk under its original name.
        const url = URL.createObjectURL(raw);
        const a = document.createElement('a');
        a.href = url;
        a.download = name;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => { try { URL.revokeObjectURL(url); } catch (_) { /* noop */ } }, 1000);
        return;
      }
      const blob = kind === 'pdf' && raw.type !== 'application/pdf'
        ? new Blob([raw], { type: 'application/pdf' })
        : raw;
      const url = URL.createObjectURL(blob);
      setNotePreview({ kind, blobUrl: url, name });
    } catch (e) {
      // Best-effort fallback — if the auth fetch fails, just open in new tab.
      window.open(`${API_URL}/api/orders/${order.id}/file/note-photo`, '_blank', 'noopener');
    }
  }, [order?.id, noteAttachment, token]);

  // Shared chip for the "Description from customer" block (rendered for both
  // the customer and the admin layout below).
  const renderNoteAttachment = () => {
    if (!noteAttachment) return null;
    const { name, kind } = noteAttachment;
    const style = kind === 'pdf'
      ? 'border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100'
      : kind === 'image'
        ? 'border-cyan-200 bg-cyan-50 text-cyan-700 hover:bg-cyan-100'
        : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50';
    const Icon = kind === 'image' ? ImageIcon : FileText;
    const Action = kind === 'file' ? Download : Eye;
    return (
      <button
        type="button"
        onClick={openNotePreview}
        data-testid="order-customer-note-photo-link"
        title={kind === 'file'
          ? tt('drawer.download_attachment', 'Download attachment')
          : tt('drawer.preview_attachment', 'Preview attachment')}
        className={`mt-2 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-all ${style}`}
      >
        <Icon className="w-3.5 h-3.5 flex-shrink-0" />
        <span className="truncate max-w-[260px]">{name}</span>
        <Action className="w-3.5 h-3.5 opacity-70 flex-shrink-0" />
      </button>
    );
  };
  // Revoke the blob URL when the preview is closed.
  useEffect(() => {
    if (!notePreview?.blobUrl) return undefined;
    return () => { try { URL.revokeObjectURL(notePreview.blobUrl); } catch (_) { /* noop */ } };
  }, [notePreview?.blobUrl]);

  // Mobile-only tab switch — without this, on a 700px-tall iPhone the chat
  // panel is pushed below the order-details column (single-column layout)
  // and the input box ends up below the viewport, making it impossible to
  // type a reply. Desktop keeps the side-by-side grid unchanged.
  const [mobileTab, setMobileTab] = useState('chat');

  // AI assist
  const [aiSettings, setAiSettings] = useState(null);
  const [improving, setImproving] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null); // string with proposed improved text
  const [translateMenu, setTranslateMenu] = useState(null);  // msg.id whose menu is open
  const [translateMenuPos, setTranslateMenuPos] = useState(null); // {top, left} in viewport (for portal)
  const [translating, setTranslating] = useState(null);   // msg.id while in flight
  // Track per-legacy-message-id download state for the "Download via
  // Legacy API" button on legacy ticket attachments. We use a Set
  // because multiple attachments may be in flight simultaneously.
  const [legacyDownloading, setLegacyDownloading] = useState(() => new Set());
  const [translations, setTranslations] = useState({});    // { msgId: { lang, translated } }
  const [userLang, setUserLang] = useState(localStorage.getItem('translatePref') || 'ru');

  useEffect(() => {
    fetch(`${API_URL}/api/service/ai-settings`)
      .then(r => r.ok ? r.json() : null)
      .then(j => setAiSettings(j))
      .catch(() => {});
  }, []);

  const aiImprove = useCallback(async () => {
    if (!draft.trim() || !isAdmin) return;
    setImproving(true); setAiSuggestion(null);
    try {
      const r = await fetch(`${API_URL}/api/service/ai/improve`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: draft.trim() }),
      });
      const j = await r.json();
      // New shape: { ok, improved | error }. Legacy shape: { improved }.
      if (r.ok && j.improved) setAiSuggestion(j.improved);
      else alert('AI error: ' + (j.error || j.detail || `HTTP ${r.status}`));
    } catch (e) { alert('AI error: ' + e.message); }
    finally { setImproving(false); }
  }, [draft, isAdmin, token]);

  const aiTranslate = useCallback(async (msg, targetLang) => {
    setTranslating(msg.id); setTranslateMenu(null); setTranslateMenuPos(null);
    try {
      const r = await fetch(`${API_URL}/api/service/ai/translate-message`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: msg.text || '', target_language: targetLang }),
      });
      // Defensive parsing: when nginx returns its 404/502 HTML page
      // (translate route not proxied yet, or backend restarted mid-
      // request), `r.json()` throws "Unexpected token '<'" and the
      // user sees a cryptic stack trace instead of "what to do next".
      // Read as text first, then try to parse — surfaces a friendly
      // message when the body isn't JSON.
      const raw = await r.text();
      let j = null;
      try { j = raw ? JSON.parse(raw) : {}; } catch { /* not JSON */ }
      if (r.ok && j && typeof j.translated === 'string') {
        setTranslations(prev => ({ ...prev, [msg.id]: { lang: j.target_language || targetLang, translated: j.translated } }));
        if (!isAdmin) {
          localStorage.setItem('translatePref', targetLang);
          setUserLang(targetLang);
        }
        return;
      }
      if (!j) {
        alert(
          `Translation service is currently unavailable (HTTP ${r.status}). ` +
          `Please retry in a few seconds; if it persists, ask the admin to ` +
          `verify that AI assistance is enabled in Settings → AI assistant.`
        );
        return;
      }
      // New JSON shape: { ok:false, error:"..." }. Legacy: { detail:"..." }.
      alert('Translate error: ' + (j.error || j.detail || `HTTP ${r.status}`));
    } catch (e) {
      alert('Translate error: ' + (e.message || 'network failure'));
    } finally { setTranslating(null); }
  }, [token, isAdmin]);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const loadMessages = useCallback(async () => {
    if (!order?.id) return;
    try {
      const res = await fetch(`${API_URL}/api/orders/${order.id}/messages`, { headers });
      if (!res.ok) return;
      const data = await res.json();
      setMessages(Array.isArray(data) ? data : []);
      // Level-1 refresh: the GET above also marks any incoming messages
      // as read server-side. Fire the global unread bus so sidebar
      // badges (Tickets counter, Orders row highlights, header bell)
      // update INSTANTLY — no 15-second polling wait.
      triggerUnreadRefresh();
    } catch (_) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.id, token]);

  useEffect(() => { loadMessages(); }, [loadMessages]);

  // Probe for legacy conversation linked to this order. Admin uses the
  // admin route; customer uses the customer-scoped endpoint. Both return
  // a list and we pick the first match (one ticket per legacy order).
  useEffect(() => {
    const lid = order?.legacy_order_id;
    if (!lid) { setLegacyTicket(null); setLegacyMessages([]); return; }
    let active = true;
    const url = isAdmin
      ? `${API_URL}/api/admin/legacy/tickets?legacy_order_id=${encodeURIComponent(lid)}`
      : `${API_URL}/api/customer/legacy/tickets?legacy_order_id=${encodeURIComponent(lid)}`;
    (async () => {
      try {
        const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        if (!r.ok) { setLegacyTicket(null); setLegacyMessages([]); return; }
        const data = await r.json();
        // The endpoint is filtered by legacy_order_id so the single
        // matching ticket is the first (and usually only) item.
        // Fall back to the strict find() match for safety.
        const match = (data.items || []).find((t) => t.legacy_order_id === lid)
                      || (data.items || [])[0];
        if (!active || !match) { setLegacyTicket(null); setLegacyMessages([]); return; }
        setLegacyTicket(match);
        // Fetch the actual messages and adapt them to the regular chat
        // bubble shape so the existing render loop works unchanged.
        const msgUrl = isAdmin
          ? `${API_URL}/api/admin/legacy/tickets/${encodeURIComponent(match.legacy_ticket_id)}/messages`
          : `${API_URL}/api/customer/legacy/tickets/${encodeURIComponent(match.legacy_ticket_id)}/messages`;
        const mr = await fetch(msgUrl, { headers: { Authorization: `Bearer ${token}` } });
        if (!mr.ok) { setLegacyMessages([]); return; }
        const mdata = await mr.json();
        // Decode the few HTML entities the old PHP serializer emitted
        // into the text column. We only handle the trio that actually
        // shows up in the dump (`&#039;`, `&amp;`, `&#8364;` plus the
        // common `&quot;` / `&lt;` / `&gt;`) — the rest were already
        // raw unicode in the CSV.
        const _decodeLegacy = (s) => {
          if (!s) return '';
          return s
            .replace(/&#039;|&#39;/g, "'")
            .replace(/&quot;/g, '"')
            .replace(/&#8364;/g, '€')
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&amp;/g, '&');
        };
        const adapted = (mdata.items || []).map((m) => {
          const senderRole = m.sender_type === 'admin' ? 'admin'
                          : m.sender_type === 'customer' ? 'user'
                          : 'system';   // staff_or_unknown → neutral
          const senderName = m.sender_type === 'admin' ? 'AUTO-REMAP'
                          : m.sender_type === 'customer'
                              ? ([order.customer_first_name, order.customer_surname].filter(Boolean).join(' ') || 'Customer')
                          : 'Staff';
          // Old PHP UI submitted `text="file"` as a placeholder for
          // attachment-only messages. Strip it so the bubble doesn't
          // render the literal word "file" with no other content.
          let txt = _decodeLegacy(m.message_text);
          if (m.attachment_path && txt.trim().toLowerCase() === 'file') {
            txt = '';
          }
          return {
            id: `legacy-${m.id}`,
            sender_role: senderRole,
            sender_name: senderName,
            // Backend (`_enrich_legacy_message_avatars`) stamps a
            // `sender_avatar_url` based on sender_type — customer side
            // gets the order owner's avatar, admin side gets the
            // "support persona" avatar. Falls back to initials.
            sender_avatar_url: m.sender_avatar_url || null,
            // The chat bubble template reads `m.text`, NOT `m.content`
            // — keep both for backward compat across new/legacy rows.
            text: txt,
            content: txt,
            created_at: m.legacy_created_at || null,
            is_legacy: true,
            is_system: senderRole === 'system',  // re-uses the slate pill style
            legacy_attachment_path: m.attachment_path || null,
            legacy_message_id: m.legacy_message_id || null,
            legacy_type_id: m.legacy_type_id,
            legacy_downloads: m.downloads,
          };
        });
        if (active) setLegacyMessages(adapted);
      } catch { if (active) { setLegacyTicket(null); setLegacyMessages([]); } }
    })();
    return () => { active = false; };
  }, [order?.legacy_order_id, isAdmin, token, order?.customer_first_name, order?.customer_surname]);

  // Real-time updates via Socket.IO — when the backend broadcasts a new
  // `ticket:message`, merge it into local state (deduped by id). The 8-second
  // REST poll below stays as a belt-and-braces fallback for cases where the
  // WebSocket handshake is blocked (some corporate proxies), so message
  // delivery is still guaranteed.
  //
  // `onTyping` surfaces the other participant's keystrokes so the chat can
  // render a "… is typing" line above the composer. We ignore our own role
  // (the backend already `skip_sid`s the sender, but we double-check here
  // for admins impersonating via a second tab or similar edge cases).
  const [peerTyping, setPeerTyping] = useState(null); // { sender_role, sender_name } | null
  const peerTypingTimerRef = useRef(null);
  const ownRole = isAdmin ? 'admin' : 'user';
  const { emitTyping } = useTicketSocket({
    orderId: order?.id,
    token,
    onMessage: (msg) => {
      if (!msg || !msg.id) { loadMessages(); return; }
      setMessages((prev) => {
        if (prev.some((m) => m.id === msg.id)) return prev;
        return [...prev, msg].sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
      });
    },
    onTyping: (payload) => {
      if (!payload || payload.sender_role === ownRole) return;
      if (payload.is_typing === false) {
        setPeerTyping(null);
        if (peerTypingTimerRef.current) { clearTimeout(peerTypingTimerRef.current); peerTypingTimerRef.current = null; }
        return;
      }
      setPeerTyping({ sender_role: payload.sender_role, sender_name: payload.sender_name });
      // Auto-hide 4s after the last typing event — senders emit at most once
      // per 2s so this gives a 2s grace period before the dots disappear.
      if (peerTypingTimerRef.current) clearTimeout(peerTypingTimerRef.current);
      peerTypingTimerRef.current = setTimeout(() => setPeerTyping(null), 4000);
    },
  });

  // Debounced typing emitter — fire when the user types, at most once per 2s.
  // Also send `is_typing: false` 2s after the user stops so the peer's
  // indicator disappears promptly even if they're not about to send.
  const lastTypingSentRef = useRef(0);
  const stopTypingTimerRef = useRef(null);
  const notifyTyping = useCallback(() => {
    const now = Date.now();
    if (now - lastTypingSentRef.current > 2000) {
      lastTypingSentRef.current = now;
      emitTyping(true);
    }
    if (stopTypingTimerRef.current) clearTimeout(stopTypingTimerRef.current);
    stopTypingTimerRef.current = setTimeout(() => { emitTyping(false); }, 2500);
  }, [emitTyping]);
  useEffect(() => () => {
    if (peerTypingTimerRef.current) clearTimeout(peerTypingTimerRef.current);
    if (stopTypingTimerRef.current) clearTimeout(stopTypingTimerRef.current);
  }, []);

  // Poll for new messages — fallback. Interval bumped from 8s → 30s since
  // the socket now carries realtime deltas; this poll only catches cases
  // where the socket is unavailable.
  useEffect(() => {
    if (!order?.id) return;
    const id = setInterval(loadMessages, 30000);
    return () => clearInterval(id);
  }, [order?.id, loadMessages]);

  // Auto-scroll chat to bottom on new messages
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollTop = messagesEndRef.current.scrollHeight;
    }
  }, [messages.length]);

  const sendMessage = async () => {
    const text = draft.trim();
    if (!text && !attachment) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('text', text);
      if (attachment) fd.append('file', attachment);
      // Internal-note flag is admin-only. Backend rejects it from
      // customer accounts with a 403; we still gate the UI here so
      // the toggle's state can't accidentally leak across a role
      // switch in dev/QA.
      if (isAdmin && internalNote) fd.append('is_internal', 'true');
      const res = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${API_URL}/api/orders/${order.id}/messages`, true);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.timeout = 60000;
        xhr.onload = () => resolve({ status: xhr.status, ok: xhr.status >= 200 && xhr.status < 300, text: xhr.responseText });
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send(fd);
      });
      if (!res.ok) {
        let detail = res.text;
        try { detail = JSON.parse(res.text)?.detail || detail; } catch (_) {}
        throw new Error(typeof detail === 'string' ? detail : 'Send failed');
      }
      setDraft('');
      setAttachment(null);
      setInternalNote(false);
      loadMessages();
    } catch (e) {
      alert('Failed to send: ' + (e?.message || String(e)));
    } finally {
      setSending(false);
    }
  };

  // Authenticated download — turns into a Blob URL we can show inline
  // (track=false: viewing inline must NOT count as a download)
  const [imageUrls, setImageUrls] = useState({});
  useEffect(() => {
    let alive = true;
    (async () => {
      const newUrls = { ...imageUrls };
      for (const m of messages) {
        if (m.attachment?.is_image && !newUrls[m.id]) {
          try {
            const res = await fetch(`${API_URL}/api/orders/${order.id}/messages/${m.id}/attachment?track=false`, { headers });
            if (res.ok) {
              const blob = await res.blob();
              if (!alive) return;
              newUrls[m.id] = URL.createObjectURL(blob);
            }
          } catch (_) {}
        }
      }
      if (alive) setImageUrls(newUrls);
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, order?.id]);

  const downloadAttachment = async (m) => {
    try {
      const res = await fetch(`${API_URL}/api/orders/${order.id}/messages/${m.id}/attachment`, { headers });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = m.attachment.name; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      loadMessages(); // refresh download counter
    } catch (e) { alert(e.message); }
  };

  const startEdit = (m) => {
    setEditingId(m.id);
    setEditingText(m.text || '');
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditingText('');
  };
  const saveEdit = async (m) => {
    const newText = editingText.trim();
    if (!newText && !m.attachment) {
      alert('Message text cannot be empty');
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/orders/${order.id}/messages/${m.id}`, {
        method: 'PATCH',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: newText }),
      });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || 'Edit failed');
      }
      setEditingId(null);
      setEditingText('');
      loadMessages();
    } catch (e) {
      alert('Failed to edit: ' + (e?.message || String(e)));
    }
  };
  const deleteMessage = async (m) => {
    if (!window.confirm('Delete this message? This cannot be undone.')) return;
    try {
      const res = await fetch(`${API_URL}/api/orders/${order.id}/messages/${m.id}`, {
        method: 'DELETE',
        headers,
      });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || 'Delete failed');
      }
      loadMessages();
    } catch (e) {
      alert('Failed to delete: ' + (e?.message || String(e)));
    }
  };

  if (!order) return null;

  const downloadFile = async (kind) => {
    try {
      const res = await fetch(`${API_URL}/api/orders/${order.id}/file/${kind}`, { headers });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const filename = (kind === 'original' ? order.original_file_name : order.modified_file_name) || `${kind}_${order.id}.bin`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('Download failed: ' + (e?.message || String(e)));
    }
  };

  // WinOLS .ini — generated server-side from the order metadata. Auth-fetch
  // it as a blob so we can force the filename = <order_number>.ini.
  const downloadIni = async () => {
    try {
      const res = await fetch(`${API_URL}/api/orders/${order.id}/ini`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const filename = `${order.order_number || order.id}.ini`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('INI download failed: ' + (e?.message || String(e)));
    }
  };

  const uploadModified = async (event) => {
    const f = event.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const res = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${API_URL}/api/orders/${order.id}/file/modified`, true);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.timeout = 120000;
        xhr.onload = () => resolve({ status: xhr.status, ok: xhr.status >= 200 && xhr.status < 300, text: xhr.responseText });
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send(fd);
      });
      const raw = res.text || '';
      let data = null;
      try { data = raw ? JSON.parse(raw) : null; } catch (_) {}
      if (!res.ok) throw new Error(data?.detail || raw || `HTTP ${res.status}`);
      // Silent success — the Tuning Files panel re-renders via onChanged()
      // showing the modified file with size + delete + download. The old
      // window.alert() was an extra "OK" click on every upload which got
      // in the way of the natural flow (admin's next action is just to
      // hit "Mark as completed"). Surface a non-blocking toast instead
      // so there's still visual confirmation on slow / failed renders.
      toast.success(`Modified file uploaded${data?.status ? ` · ${data.status}` : ''}`);
      onChanged && onChanged();
    } catch (e) {
      toast.error('Upload failed: ' + (e?.message || String(e)));
    } finally {
      setUploading(false);
      try { event.target.value = ''; } catch (_) {}
    }
  };

  const markCompleted = async () => {
    if (!window.confirm(`Mark order ${order.order_number || order.id} as COMPLETED?\n\nThe customer's reserved balance will be settled and a transaction recorded.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/orders/${order.id}/status`, {
        method: 'PATCH',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'completed' }),
      });
      const raw = await res.text();
      const data = raw ? JSON.parse(raw) : null;
      if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
      onChanged && onChanged();
    } catch (e) { alert('Failed: ' + e.message); }
  };

  // Admin-only: cancel the order and refund the full amount back to the
  // customer's balance. Works on ANY status (including completed) — the
  // backend's `refund_order` helper handles each scenario:
  //  • pending/open    → release the reservation back to balance
  //  • completed       → reverse the settled charge back to balance and
  //                       promote status to `closed` (preserves "completed
  //                       but later refunded" semantics)
  //  • already cancelled → noop (safeguarded server-side)
  const cancelAndRefund = async () => {
    const total = Number(order.total_price || 0).toFixed(2);
    const isCompleted = order.status === 'completed';
    const verb        = isCompleted ? 'Close' : 'Cancel';
    const newStatus   = isCompleted ? 'closed' : 'cancelled';
    const confirmed = window.confirm(
      `${verb} order ${order.order_number || order.id} and REFUND €${total} to the customer?\n\n` +
      `Status will be set to "${newStatus}" and the full amount returned to the customer's balance.`
    );
    if (!confirmed) return;
    // Capture the reason — surfaced in the ticket thread and in the
    // cancellation email so the customer knows why.
    //
    // window.prompt returns `null` when the user clicks Cancel and
    // an empty string when they click OK without typing. We MUST
    // distinguish the two: a null means "abort", an empty string
    // means "proceed without a reason". The previous version
    // collapsed both to '' which silently went through with the
    // refund — exactly the case the operator hit.
    const reasonRaw = window.prompt(
      `Reason for ${verb.toLowerCase()} (sent to the customer by email):`,
      ''
    );
    if (reasonRaw === null) return;     // user clicked Cancel on the prompt
    const reason = reasonRaw.trim();
    try {
      const res = await fetch(`${API_URL}/api/orders/${order.id}/status`, {
        method: 'PATCH',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'cancelled', reason }),
      });
      const raw = await res.text();
      const data = raw ? JSON.parse(raw) : null;
      if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
      try {
        const { toast } = await import('sonner');
        const finalStatus = (data && data.status) || newStatus;
        toast.success(`Order ${finalStatus === 'closed' ? 'closed' : 'cancelled'} — €${total} refunded to customer.`);
      } catch (_) { /* noop */ }
      onChanged && onChanged();
    } catch (e) { alert(`${verb} & refund failed: ` + e.message); }
  };

  // Sibling of cancelAndRefund — same refund + email-reason flow, but
  // ALSO hard-deletes the order doc, its ticket thread, and its
  // original-file refs so no row remains in the orders list. Audit
  // trail is preserved in `deleted_orders_log` server-side.
  const cancelAndDelete = async () => {
    const total = Number(order.total_price || 0).toFixed(2);
    const confirmed = window.confirm(
      `DELETE order ${order.order_number || order.id} permanently?\n\n` +
      `• €${total} will be refunded to the customer's balance\n` +
      `• Order, chat history, and uploaded files will be REMOVED from the system\n` +
      `• This action cannot be undone (audit log keeps a forensic record).`
    );
    if (!confirmed) return;
    const reasonRaw = window.prompt(
      'Reason for deleting (sent to the customer by email):',
      ''
    );
    if (reasonRaw === null) return;     // user clicked Cancel on the prompt
    const reason = reasonRaw.trim();
    try {
      const res = await fetch(`${API_URL}/api/orders/${order.id}/cancel-and-delete`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
      try {
        const { toast } = await import('sonner');
        toast.success(`Order deleted — €${data.refunded_amount?.toFixed?.(2) || total} refunded.`);
      } catch (_) { /* noop */ }
      onClose && onClose();
      onChanged && onChanged();
    } catch (e) { alert('Cancel & delete failed: ' + e.message); }
  };

  return (
    <div
      className="fixed inset-0 z-30 flex bg-slate-50 pt-[108px] md:pt-[140px]"
      data-testid="order-detail-drawer"
    >
      <div className="relative w-full lg:w-[85%] mx-auto bg-slate-50 h-full overflow-hidden shadow-2xl flex flex-col">
        <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white px-4 md:px-6 py-3 md:py-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2 md:gap-4 min-w-0 flex-1">
            <div className="min-w-0">
              <h2 className="text-sm md:text-lg font-bold flex items-center gap-1.5 md:gap-2 truncate">
                <FileText className="w-4 h-4 md:w-5 md:h-5 flex-shrink-0" />
                {(() => {
                  // Click the number → copy raw number (no '#', no 'No.')
                  // to clipboard. Works on both mobile and desktop so
                  // admins can grab the ID without leaving the drawer.
                  const num = order.order_number || order.id?.slice(0, 8) || '';
                  const copyNum = () => {
                    if (!num) return;
                    const clean = String(num).replace(/^#/, '');
                    navigator.clipboard?.writeText(clean).then(() => {
                      import('sonner').then(({ toast }) => toast.success(
                        isAdmin ? `Copied: ${clean}` : t('drawer.copied_value', { value: clean, defaultValue: `Copied: ${clean}` })
                      ));
                    }).catch(() => {
                      import('sonner').then(({ toast }) => toast.error(tt('drawer.copy_failed', 'Copy failed')));
                    });
                  };
                  return (
                    <>
                      <button
                        type="button"
                        onClick={copyNum}
                        title={tt('drawer.click_copy_order_number', 'Click to copy order number (without #)')}
                        className="md:hidden text-left hover:underline decoration-dotted underline-offset-2 cursor-pointer truncate"
                        data-testid="drawer-header-order-number-mobile"
                      >
                        № {num}
                      </button>
                      <button
                        type="button"
                        onClick={copyNum}
                        title={tt('drawer.click_copy_order_number', 'Click to copy order number (without #)')}
                        className="hidden md:inline-flex text-left hover:underline decoration-dotted underline-offset-2 cursor-pointer"
                        data-testid="drawer-header-order-number"
                      >
                        {tt('drawer.detail_for_order', 'Detail for order')} {num}
                      </button>
                    </>
                  );
                })()}
              </h2>
              <p className="text-[10px] md:text-xs text-slate-300 mt-0.5">{fmt(order.created_at)}</p>
            </div>
            {/* Edit + Actions sit next to the title (left-aligned) so the
                long stretch of empty header between the title and the
                close-× isn't wasted. Pushed to flex-shrink-0 so they
                stay readable when the title truncates. */}
            <div className="flex items-center gap-1.5 md:gap-2 flex-shrink-0" data-testid="drawer-header-actions">
              {/* Edit (admin) — icon-only on mobile, icon+label on desktop. */}
              {isAdmin && onEditOrder && order.status !== 'closed' && (
                <button
                  onClick={() => { onEditOrder(order); onClose && onClose(); }}
                  data-testid="drawer-edit-order-btn"
                  className="flex items-center justify-center md:gap-1.5 px-2 md:px-3 py-1.5 md:py-2 rounded-lg bg-amber-500/90 hover:bg-amber-500 text-white text-xs md:text-sm font-bold shadow-md"
                  title="Edit this order — works on any status, including completed"
                  aria-label="Edit"
                >
                  <Pencil className="w-4 h-4" />
                  <span className="hidden md:inline">Edit</span>
                </button>
              )}
              {/* Edit (customer) — same UX as admin's, but only while order
                  is editable (status pending/open/received AND !ready_at). */}
              {!isAdmin && onEditOrder && ['pending', 'open', 'received'].includes(order.status) && !order.ready_at && (
                <button
                  onClick={() => { onEditOrder(order); onClose && onClose(); }}
                  data-testid="drawer-edit-order-btn-customer"
                  className="flex items-center justify-center md:gap-1.5 px-2 md:px-3 py-1.5 md:py-2 rounded-lg bg-amber-500/90 hover:bg-amber-500 text-white text-xs md:text-sm font-bold shadow-md"
                  title={tt('drawer.edit_order_tooltip', 'Edit this order before the engineer starts working on it')}
                  aria-label={tt('drawer.edit', 'Edit')}
                >
                  <Pencil className="w-4 h-4" />
                  <span className="hidden md:inline">{tt('drawer.edit', 'Edit')}</span>
                </button>
              )}
              {/* Back — sits between Edit and Actions per admin request.
                  Icon-only on mobile, icon+label on desktop. */}
              <button
                onClick={onClose}
                data-testid="back-order-btn"
                className="flex items-center justify-center md:gap-1.5 px-2 md:px-3 py-1.5 md:py-2 rounded-lg bg-white/10 hover:bg-white/20 text-sm font-medium flex-shrink-0"
                title={tt('drawer.back_to_orders', 'Back to orders')}
                aria-label={tt('drawer.back_to_orders', 'Back to orders')}
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden md:inline">{tt('drawer.back', 'Back')}</span>
              </button>
              {/* Actions dropdown (admin) — visible on mobile + desktop. */}
              {isAdmin && (() => {
                const alreadyCancelled = order.status === 'cancelled';
                const closed           = order.status === 'closed';
                const isCompleted      = order.status === 'completed';
                const disabled         = alreadyCancelled || closed;
                const label = alreadyCancelled
                  ? 'Cancelled'
                  : closed
                    ? 'Closed'
                    : isCompleted
                      ? 'Close & Refund'
                      : 'Cancel & Refund';
                const reason = alreadyCancelled
                  ? 'This order is already cancelled — nothing to refund.'
                  : closed
                    ? 'This order is already closed.'
                    : isCompleted
                      ? 'Close completed order and refund the full amount back to the customer\'s balance'
                      : 'Cancel order and refund the full amount back to the customer\'s balance';
                return (
                  <div className="relative" data-testid="drawer-actions-menu-wrap">
                    <button
                      type="button"
                      onClick={() => setActionsMenu((v) => !v)}
                      data-testid="drawer-actions-menu-toggle"
                      className="flex items-center gap-1.5 px-2.5 md:px-3 py-1.5 md:py-2 rounded-lg bg-slate-700 hover:bg-slate-800 text-white text-xs md:text-sm font-bold shadow-md transition-colors"
                      title="More actions"
                      aria-expanded={actionsMenu}
                    >
                      Actions <span className="text-xs">▼</span>
                    </button>
                    {actionsMenu && (
                      <>
                        <div
                          className="fixed inset-0 z-30"
                          onClick={() => setActionsMenu(false)}
                        />
                        <div
                          className="absolute left-0 md:right-0 md:left-auto mt-1 z-40 min-w-[220px] bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden"
                          data-testid="drawer-actions-menu"
                        >
                          <button
                            onClick={() => { setActionsMenu(false); if (!disabled) cancelAndRefund(); }}
                            disabled={disabled}
                            data-testid="drawer-cancel-refund-btn"
                            title={reason}
                            className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors ${disabled ? 'text-slate-400 cursor-not-allowed' : 'text-rose-700 hover:bg-rose-50'}`}
                          >
                            <Trash2 className="w-4 h-4" />
                            <span>{label}</span>
                          </button>
                          <button
                            onClick={() => { setActionsMenu(false); cancelAndDelete(); }}
                            data-testid="drawer-cancel-delete-btn"
                            title="Refund the customer AND permanently delete the order (chat, files, all records). Audit log keeps a forensic trace."
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left text-slate-800 hover:bg-slate-100 border-t border-slate-100"
                          >
                            <Trash2 className="w-4 h-4" />
                            <span>Cancel &amp; Delete</span>
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
          <div className="flex items-center gap-1.5 md:gap-2 flex-shrink-0">
            <button onClick={onClose} data-testid="close-order-drawer" className="w-8 h-8 md:w-9 md:h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"><X className="w-4 h-4 md:w-5 md:h-5" /></button>
          </div>
        </div>

        {/* Mobile-only tab switcher: Details ↔ Chat. Hidden on lg where both
            panels are visible side-by-side. */}
        <div className="lg:hidden flex bg-white border-b border-slate-200 flex-shrink-0" data-testid="mob-tab-switch">
          <button
            type="button"
            onClick={() => setMobileTab('details')}
            data-testid="mob-tab-details"
            className={`flex-1 py-2.5 text-sm font-bold transition-colors ${mobileTab === 'details' ? 'text-cyan-700 border-b-2 border-cyan-600 bg-cyan-50/40' : 'text-slate-600'}`}
          >
            <FileText className="w-4 h-4 inline mr-1.5 -mt-0.5" /> {tt('drawer.tab_details', 'Details')}
          </button>
          <button
            type="button"
            onClick={() => setMobileTab('chat')}
            data-testid="mob-tab-chat"
            className={`flex-1 py-2.5 text-sm font-bold transition-colors flex items-center justify-center gap-1.5 ${mobileTab === 'chat' ? 'text-cyan-700 border-b-2 border-cyan-600 bg-cyan-50/40' : 'text-slate-600'}`}
          >
            <MessageSquare className="w-4 h-4" /> {tt('drawer.tab_ticket_chat', 'Ticket (chat)')}
            {messages.length > 0 && (
              <span className="min-w-[20px] h-[20px] px-1.5 rounded-full text-[10px] font-bold bg-cyan-100 text-cyan-700">
                {messages.length}
              </span>
            )}
          </button>
        </div>

        <div className="flex-1 overflow-hidden grid grid-cols-1 lg:grid-cols-12 gap-0 min-h-0">
          {/* LEFT: order details (5 cols on desktop, narrower so the chat
              has more breathing room — order info is compact, conversation
              benefits from extra width). */}
          <div className={`overflow-y-auto p-4 md:p-5 space-y-3 border-r border-slate-200 lg:col-span-5 ${mobileTab !== 'details' ? 'hidden lg:block' : ''}`}>
          {/* Compact "Variant D" header + Vehicle/Services side by side
              card. Combines the previous big stat trio (Status / Priority
              / Amount) with the order title row, cuts vertical space ~50%.
              Timeline (Created · Updated · Deadline · Ready-at editor) is
              embedded inline as a single dense strip — the old standalone
              Timeline card was deleted as part of the Order Details
              compactness pass. */}
          <section className="bg-white rounded-xl border border-slate-200 p-3.5 md:p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <button
                type="button"
                onClick={() => {
                  const num = order.order_number || order.id?.slice(0, 8) || '';
                  if (!num) return;
                  // Strip any leading "#" just in case — the user asked
                  // for the bare number so it pastes cleanly into search
                  // boxes / Excel / external systems.
                  const clean = num.startsWith('#') ? num.slice(1) : num;
                  navigator.clipboard?.writeText(clean).then(() => {
                    import('sonner').then(({ toast }) => toast.success(
                      isAdmin ? `Copied: ${clean}` : t('drawer.copied_value', { value: clean, defaultValue: `Copied: ${clean}` })
                    ));
                  }).catch(() => {
                    import('sonner').then(({ toast }) => toast.error(tt('drawer.copy_failed', 'Copy failed')));
                  });
                }}
                title={tt('drawer.click_copy_order_number', 'Click to copy order number (without #)')}
                className="text-base font-bold text-slate-800 hover:text-cyan-700 hover:underline decoration-dotted underline-offset-2 transition cursor-pointer"
                data-testid="order-detail-number"
              >
                {tt('drawer.order_prefix', 'Order')} #{order.order_number || order.id?.slice(0, 8)}
              </button>
              {/* Admin-only telemetry badge — set when the customer used
                  the inline Find-by-ECU smart selector on Step 1 and
                  pressed "Apply and continue". Hidden from customers. */}
              {isAdmin && order.created_via_ecu_finder && (
                <span
                  data-testid="order-ecu-finder-badge"
                  title="Customer used the smart ECU finder (Step 1 → Find by ECU → Apply)"
                  className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gradient-to-br from-cyan-500 to-cyan-700 text-white shadow-sm ring-1 ring-cyan-300/60"
                >
                  <Wand2 className="w-3 h-3" />
                </span>
              )}
              <span className="text-slate-400">•</span>
              <span className="text-xs text-slate-500">{fmt(order.created_at)}</span>

              {/* Compact admin-only customer chip — replaces the full
                  Customer card that used to live below Vehicle Information.
                  Shows just the customer name + balance; clicking opens the
                  full customer profile drawer (same handler as before). */}
              {isAdmin && (
                <button
                  type="button"
                  onClick={() => order.user_id && setOpenCustomerId(order.user_id)}
                  className="inline-flex items-center gap-2 px-2.5 py-1 rounded-md bg-amber-50 hover:bg-amber-100 border border-amber-200 hover:border-amber-300 transition group"
                  data-testid="order-customer-chip"
                  title="Open customer profile"
                >
                  <span className="w-5 h-5 rounded bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-sm flex-shrink-0">
                    <User className="w-3 h-3 text-white" />
                  </span>
                  <span className="flex flex-col items-start leading-tight">
                    <span className="text-xs font-semibold text-slate-800 flex items-center gap-1 min-w-0">
                      <span className="truncate max-w-[150px]" data-testid="order-customer-chip-name">
                        {[order.customer_first_name, order.customer_surname].filter(Boolean).join(' ') || order.user_email || '—'}
                      </span>
                      <CustomerBadges
                        isCompany={customerInfo?.is_company}
                        companyName={customerInfo?.company_name}
                        registrationDate={customerInfo?.registration_date}
                        newDays={newCustomerDays}
                        testid="order-customer-chip-badges"
                      />
                    </span>
                    <span className="text-[10px] text-emerald-700 font-bold tabular-nums" data-testid="order-customer-chip-balance">
                      Balance {customerInfo?.balance !== null && customerInfo?.balance !== undefined ? `€${Number(customerInfo.balance).toFixed(2)}` : '…'}
                    </span>
                  </span>
                </button>
              )}

              <span className="ml-auto inline-flex items-center gap-2 flex-wrap">
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md ${(STATUS_STYLE[order.status] || STATUS_STYLE.open).chip || 'bg-blue-50'}`}>
                  <span className={`w-2 h-2 rounded-full ${(STATUS_STYLE[order.status] || STATUS_STYLE.open).dot}`} />
                  <span className={`font-bold uppercase tracking-wider text-xs ${(STATUS_STYLE[order.status] || STATUS_STYLE.open).text || 'text-blue-600'}`} data-testid="order-detail-status">
                    {tt(`status.${order.status}`, STATUS_LABEL[order.status] || order.status)}
                  </span>
                </span>
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md ${(PRIORITY_STYLE[order.priority] || PRIORITY_STYLE.medium).chip || 'bg-amber-50'}`}>
                  <span className={`w-2 h-2 rounded-full ${(PRIORITY_STYLE[order.priority] || PRIORITY_STYLE.medium).dot || 'bg-amber-500'}`} />
                  <span className={`font-bold uppercase tracking-wider text-xs ${(PRIORITY_STYLE[order.priority] || PRIORITY_STYLE.medium).text || 'text-amber-600'}`} data-testid="order-detail-priority">
                    {tt(`priorities.${order.priority}`, PRIORITY_LABEL[order.priority] || order.priority)}
                  </span>
                </span>
                {/* Live priority countdown — same timer that's shown in the
                    OrdersTable row. Hidden automatically when status is
                    completed/cancelled, so the timer disappears the moment
                    the order closes. */}
                <PriorityCountdown
                  deadline={order.priority_deadline}
                  readyAt={order.ready_at}
                  status={order.status}
                  compact
                  isAdmin={isAdmin}
                />
                <b className="text-emerald-600 text-lg sm:text-xl font-extrabold tabular-nums" data-testid="order-detail-amount">{Number(order.total_price || 0).toFixed(0)} €</b>
              </span>
            </div>

            {/* Dense Timeline strip — replaces the old standalone Timeline
                card (~70px tall) with a single inline row. On mobile the
                pills wrap onto two lines; on desktop they sit beside each
                other. Read-only for everyone; the admin "set custom time"
                editor now lives inside the Modified file card below. */}
            <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-600 border-y border-slate-100 py-1.5" data-testid="order-detail-timeline-strip">
              {/* "Created" was removed — the creation date already lives
                  in the header right next to the order number, so showing
                  it again in the strip was pure redundancy. */}
              <span className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-blue-500" /><span className="text-slate-400 uppercase tracking-wider text-[10px] font-bold">{tt('drawer.updated', 'Updated')}</span><span className="font-semibold text-slate-700">{fmt(order.updated_at)}</span></span>
              <span className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-amber-500" /><span className="text-slate-400 uppercase tracking-wider text-[10px] font-bold">{tt('drawer.deadline', 'Deadline')}</span><span className="font-semibold text-slate-700">{fmt(order.priority_deadline)}</span></span>
              {order.modified_uploaded_at && (
                <span className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /><span className="text-slate-400 uppercase tracking-wider text-[10px] font-bold">{tt('drawer.uploaded', 'Uploaded')}</span><span className="font-semibold text-emerald-700">{fmt(order.modified_uploaded_at)}</span></span>
              )}
              <span className="ml-auto inline-flex items-center gap-1.5 max-w-full">
                {/* Admin's editor moved INTO the Modified file card (below)
                    for proximity to the file action — no more scrolling up
                    to set a custom ready-at. The strip keeps just the
                    read-only display for customers (only when ready_at is
                    actually set; ReadyAtRow returns null otherwise). */}
                {!isAdmin && <ReadyAtRow order={order} token={token} isAdmin={isAdmin} onChanged={onChanged} compact />}
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Vehicle Information (Phase 2) — the page's primary
                  workflow focus. 2-column grid with logical groupings
                  (Identity / Engine + ECU / Transmission / File-method)
                  separated by hairline dividers; VIN gets a dedicated
                  full-width highlight strip at the bottom. Values are
                  larger than labels so engineers can scan the card at
                  a glance instead of reading a long list. */}
              <div className="rounded-lg border border-slate-200 bg-white p-3.5 md:col-span-1">
                <div className="text-[11px] uppercase text-slate-700 font-extrabold tracking-wider mb-2.5 flex items-center gap-1.5">
                  <Car className="w-3.5 h-3.5 text-cyan-600" /> {tt('drawer.vehicle_information', 'Vehicle Information')}
                </div>
                {(() => {
                  const isOrig = (order.vehicle_type || '').toLowerCase() === 'original files';
                  if (isOrig) {
                    const m = order.original_files_meta || {};
                    const brand = m.brand || order.manufacturer;
                    const model = m.model || order.model;
                    const ecu = [m.ecu_brand, m.ecu_model || order.ecu_type].filter(Boolean).join(' ');
                    return (
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-x-4 gap-y-2.5">
                          <VField label={tt('fields.manufacturer', 'Manufacturer')} value={brand} emphasize />
                          <VField label={tt('fields.model', 'Model')} value={model} emphasize />
                          <VField label={tt('fields.engine', 'Engine')} value={m.engine || order.engine} />
                          <VField label={tt('fields.ecu', 'ECU')} value={ecu} />
                          <VField label={tt('fields.hardware_no', 'Hardware №')} value={m.hw_number} mono />
                          <VField label={tt('fields.software_no', 'Software №')} value={m.sw_number} mono />
                          <VField label={tt('fields.sw_upgrade', 'SW upgrade')} value={m.sw_upgrade} mono />
                          <VField label={tt('fields.calibration', 'Calibration')} value={m.calibration} mono />
                          {isAdmin && <VField label="Damos" value={m.damos} mono />}
                          {isAdmin && <VField label="Driver" value={m.driver_filename} mono />}
                        </div>
                      </div>
                    );
                  }
                  const powerStr = order.engine_power_hp
                    ? `${order.engine_power_hp} HP / ${order.engine_power_kw || ''} kW`
                    : null;
                  return (
                    <div className="space-y-3" data-testid="order-vehicle-info">
                      {/* Identity row — Manufacturer / Model / Year */}
                      <div className="grid grid-cols-2 gap-x-4 gap-y-2.5">
                        <VField label={tt('fields.manufacturer', 'Manufacturer')} value={order.manufacturer} emphasize />
                        <VField label={tt('fields.model', 'Model')} value={order.model} emphasize />
                        <VField label={tt('fields.year', 'Year')} value={order.model_year} />
                        <VField label={tt('fields.type', 'Type')} value={order.vehicle_type} />
                      </div>
                      {/* Engine + ECU group */}
                      {(order.engine || order.ecu_type) && (
                        <div className="pt-2.5 border-t border-slate-100 grid grid-cols-2 gap-x-4 gap-y-2.5">
                          <VField label={tt('fields.engine', 'Engine')} value={order.engine} emphasize />
                          <VField label={tt('fields.ecu', 'ECU')} value={order.ecu_type} emphasize />
                        </div>
                      )}
                      {/* Transmission + Power group */}
                      {(order.gearbox || powerStr) && (
                        <div className="pt-2.5 border-t border-slate-100 grid grid-cols-2 gap-x-4 gap-y-2.5">
                          <VField label={tt('fields.gearbox', 'Gearbox')} value={order.gearbox} />
                          <VField label={tt('fields.power', 'Power')} value={powerStr} />
                        </div>
                      )}
                      {/* File-method group */}
                      {(order.file_type || order.reading_method || order.checksum_correction) && (
                        <div className="pt-2.5 border-t border-slate-100 grid grid-cols-2 gap-x-4 gap-y-2.5">
                          <VField label={tt('fields.file_type_short', 'File type')} value={order.file_type} />
                          <VField label={tt('fields.read_method', 'Read method')} value={order.reading_method} />
                          <VField label={tt('fields.checksum', 'Checksum')} value={order.checksum_correction} />
                          {isAdmin && order.catalog_snapshot_id && (
                            <VField
                              label="Catalog snap"
                              mono
                              value={
                                <span
                                  title={`Source: ${order.catalog_source || 'unknown'}\nVersion: ${order.catalog_version || 'n/a'}`}
                                  data-testid="order-detail-catalog-snapshot-id"
                                >
                                  {order.catalog_snapshot_id.slice(0, 8)}…
                                </span>
                              }
                            />
                          )}
                        </div>
                      )}
                      {/* VIN — highlighted full-width strip. VIN is one of
                          the most scanned fields, so it gets a dedicated
                          monospace row with its own subtle background. */}
                      {order.vin && (
                        <div className="pt-2.5 border-t border-slate-100">
                          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1.5 flex items-center gap-1.5">
                            <Hash className="w-3 h-3 text-cyan-600" /> VIN
                          </div>
                          <div
                            className="font-mono text-sm sm:text-[15px] font-bold text-slate-900 bg-cyan-50/70 border border-cyan-200 rounded-md px-3 py-2 break-all select-all"
                            data-testid="order-detail-vin"
                          >
                            {order.vin}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* ─── Legacy migration block (admin-only debug) ─── */}
                {isAdmin && order.legacy_source === 'old_php_admin' && (
                  <LegacyOrderBlock order={order} token={token} isAdmin={isAdmin} />
                )}

              </div>
              {/* Ordered options */}
              <div className="rounded-lg border border-slate-200 bg-slate-50/40 p-3">
                <div className="text-[11px] uppercase text-slate-700 font-extrabold tracking-wider mb-2 flex items-center gap-1.5">
                  <SettingsIcon className="w-3.5 h-3.5 text-violet-600" /> {tt('drawer.ordered_options', 'Ordered options')}
                  {(() => {
                    const n = (order.service_names || order.selected_options || []).length;
                    return n > 0 ? <span className="text-slate-400 normal-case font-normal">({n})</span> : null;
                  })()}
                </div>
                <div className="space-y-1 text-xs">
                  {(() => {
                    const keys  = order.selected_options || [];
                    const names = order.service_names    || [];
                    // Choose whichever array has entries (admins may have only one).
                    const len = Math.max(keys.length, names.length);
                    if (len === 0) return <span className="text-slate-400 italic">{tt('drawer.no_services', 'No services')}</span>;
                    const items = [];
                    for (let i = 0; i < len; i++) {
                      const key = typeof keys[i] === 'string' ? keys[i] : (keys[i]?.id || keys[i]?.key);
                      const name = names[i] || keys[i]?.name || key || '—';
                      items.push(
                        <div key={i} className="flex items-center gap-2 py-0.5">
                          <ServiceIcon optionKey={key} fallback={name} />
                          <span className="flex-1 truncate text-slate-700">{name}</span>
                        </div>
                      );
                    }
                    return items;
                  })()}
                </div>
                {/* Variant 4 — Big Order total left, optional savings hint
                    on the right. NOTE: in this system `total_price` is the
                    final amount the customer pays (discount has already
                    been applied). `discount` is an informational "you
                    saved €X" figure — never subtract it from total_price. */}
                {(() => {
                  const total    = Number(order.total_price ?? 0);
                  const discount = Number(order.discount     ?? 0);
                  // Compute the *original* price as total + discount only
                  // when both are positive — that's the most consistent
                  // interpretation of "what the customer would have paid
                  // without any discount". Stored `subtotal` is unreliable
                  // (legacy imports often store gross-with-discount).
                  const original = discount > 0 ? total + discount : null;
                  return (
                    <div className="mt-3 pt-3 border-t border-slate-200 flex items-center justify-between gap-3">
                      <div>
                        <div className="text-[10px] uppercase text-slate-400 tracking-wider font-bold">{tt('drawer.order_total', 'Order total')}</div>
                        <div className="text-2xl sm:text-3xl font-extrabold text-emerald-600 leading-none tabular-nums" data-testid="order-total">
                          €{total.toFixed(total % 1 === 0 ? 0 : 2)}
                        </div>
                      </div>
                      {discount > 0 && (
                        <div className="text-[10px] text-right text-slate-500 leading-snug" data-testid="order-discount-breakdown">
                          {original !== null && <div>{tt('drawer.was', 'Was')} <span className="line-through tabular-nums">€{original.toFixed(0)}</span></div>}
                          <div>{tt('drawer.saved', 'Saved')} <b className="text-emerald-600 tabular-nums">€{discount.toFixed(0)}</b></div>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          </section>

          {/* Customer-side "context row" (combined block).
              Pairs Description-from-customer (what the customer told us
              when ordering) with Instructions-for-this-order (what we
              tell the customer about flashing / where to download the
              guide). Both are short, complementary, and complete the
              conversation around the order — so they share one row at
              50/50 on desktop and stack on mobile. Admin keeps these
              separate because the admin Instructions surface is a full
              textarea editor that needs its own breathing room. */}
          {!isAdmin ? (
            <div className="flex flex-col md:flex-row gap-3" data-testid="customer-context-row">
              {(order.customer_note || order.customer_note_photo_name) && (
                <section
                  className="bg-amber-50/60 rounded-xl border border-amber-200 p-3.5 shadow-sm md:flex-1 md:basis-0 min-w-0"
                  data-testid="order-customer-note"
                >
                  <h3 className="text-sm font-bold text-amber-900 mb-2 flex items-center gap-2">
                    <span className="w-7 h-7 rounded-lg bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-sm">
                      <MessageSquare className="w-3.5 h-3.5 text-white" />
                    </span>
                    {tt('drawer.description_from_customer', 'Description from customer')}
                  </h3>
                  {order.customer_note && (
                    <p
                      className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed"
                      data-testid="order-customer-note-text"
                    >
                      {order.customer_note}
                    </p>
                  )}
                  {order.customer_note_photo_name && renderNoteAttachment()}
                </section>
              )}
              {/* Instructions-for-this-order strip — rendered inline here
                  for the customer so it pairs visually with the description
                  it complements. `noMargin` strips the default `mt-3` so
                  it sits flush in the flex row. `className` applies the
                  flex-1 sizing directly to the strip's root section, so
                  when it returns null (no instructions yet) no empty cell
                  is left behind — Description naturally expands to fill. */}
              <InstructionsBlock
                token={token}
                order={order}
                isAdmin={false}
                isCustomer
                noMargin
                className="md:flex-1 md:basis-0 min-w-0"
              />
            </div>
          ) : (
            (order.customer_note || order.customer_note_photo_name) && (
              <section
                className="bg-amber-50/60 rounded-xl border border-amber-200 p-3.5 shadow-sm"
                data-testid="order-customer-note"
              >
                <h3 className="text-sm font-bold text-amber-900 mb-2 flex items-center gap-2">
                  <span className="w-7 h-7 rounded-lg bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow-sm">
                    <MessageSquare className="w-3.5 h-3.5 text-white" />
                  </span>
                  Description from customer
                </h3>
                {order.customer_note && (
                  <p
                    className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed"
                    data-testid="order-customer-note-text"
                  >
                    {order.customer_note}
                  </p>
                )}
                {order.customer_note_photo_name && renderNoteAttachment()}
              </section>
            )
          )}

          {/* Customer info is now rendered as a compact chip inside the
              order header (top of this column) instead of a standalone
              section. Clicking that chip opens the full customer profile
              drawer with all email/country/IP/messenger details — same
              flow as before, just less vertical space on the order page. */}

          {/* Tuning Files (Phase 2 redesign) — symmetric Original /
              Modified cards with the same visual structure. File name
              and size dominate the card; actions live in a single
              horizontal wrap-friendly row (Download · Replace · Complete)
              instead of a tall stack. Attachments (WinOLS .ini and any
              future operator artefacts) render as a lightweight list
              inside the Modified card, NOT as another full-width button.
              Original-files purchases (where customer never uploads) keep
              the single-card variant via the isOrigFiles branch. */}
          <div className="space-y-3">
          <div>
          {(() => {
            const isOrigFiles = (order.vehicle_type || '').toLowerCase() === 'original files';

            // Shared action-button styles so Original/Modified look
            // visually consistent across both cards.
            const ACTION_PRIMARY = 'inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold text-white shadow-sm transition';
            const ACTION_SECONDARY = 'inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border bg-white text-slate-700 hover:bg-slate-50 transition';

            return (
          <>
          <section className="bg-white rounded-xl border border-slate-200 p-3.5 shadow-sm hover:shadow-md transition-shadow" data-testid="order-tuning-files">
            <h3 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2.5">
              <span className={`w-7 h-7 rounded-lg ${isOrigFiles ? 'bg-gradient-to-br from-blue-500 to-cyan-600' : 'bg-gradient-to-br from-emerald-500 to-teal-600'} flex items-center justify-center shadow-sm`}>
                <FileText className="w-3.5 h-3.5 text-white" />
              </span>
              {isOrigFiles ? tt('drawer.original_file_section', 'Original File') : tt('drawer.tuning_files', 'Tuning Files')}
            </h3>

            <div className={`grid grid-cols-1 ${isOrigFiles ? '' : 'md:grid-cols-2'} gap-3`}>
              {/* ───── ORIGINAL (from customer) — only for normal tuning jobs ───── */}
              {!isOrigFiles && (
                <div className="border border-slate-200 rounded-lg p-3 flex flex-col" data-testid="file-original-card">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1.5">{tt('drawer.original_file', 'Original file')}</div>
                  {order.original_file_name ? (
                    <>
                      <div className="text-sm font-semibold text-slate-900 break-all leading-snug" title={order.original_file_name}>
                        {order.original_file_name}
                      </div>
                      <div className="text-xs text-slate-500 mb-3 tabular-nums">{formatBytes(order.original_file_size)}</div>
                      <div className="flex flex-wrap gap-1.5">
                        <button
                          onClick={() => downloadFile('original')}
                          data-testid="download-original-btn"
                          className={`${ACTION_SECONDARY} border-slate-300 flex-1 min-w-[120px]`}
                        >
                          <Download className="w-3.5 h-3.5" /> {tt('drawer.download', 'Download')}
                        </button>
                      </div>
                    </>
                  ) : order.legacy_order_id ? (
                    <LegacyFileDownloadButton
                      token={token}
                      orderId={order.id}
                      kind="original"
                      label={isAdmin ? 'Download original (legacy)' : tt('drawer.download_original', 'Download original')}
                      hintPath={isAdmin ? order.legacy_file_default_path : null}
                      testid="download-legacy-original-btn"
                    />
                  ) : (
                    <p className="text-xs text-slate-400 italic">{tt('drawer.no_original_file', 'No original file')}</p>
                  )}

                  {/* ───── Attachments (WinOLS .ini) — relocated here from
                      the Modified file card. The original-file column has
                      fewer action buttons than Modified (especially after
                      the order is completed), so vertical real estate
                      goes much further here. The .ini is auto-generated
                      server-side from order metadata and is admin-only.
                      Same behaviour and same `data-testid="download-ini-btn"`
                      so any existing automation continues to work. */}
                  {isAdmin && (
                    <div className="mt-3 pt-2.5 border-t border-slate-200" data-testid="modified-attachments">
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1.5">{tt('drawer.attachments', 'Attachments')}</div>
                      <button
                        onClick={() => downloadIni()}
                        data-testid="download-ini-btn"
                        title="Download WinOLS .ini config"
                        className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-indigo-50 text-left transition group"
                      >
                        <FileCog className="w-3.5 h-3.5 text-indigo-600 flex-shrink-0" />
                        <span className="text-xs font-medium text-slate-800 group-hover:text-indigo-700 truncate">
                          {order.order_number ? `${order.order_number}.ini` : 'WinOLS.ini'}
                        </span>
                        <span className="ml-auto text-[10px] text-slate-400 uppercase tracking-wider">WinOLS</span>
                        <Download className="w-3 h-3 text-slate-400 group-hover:text-indigo-600 flex-shrink-0" />
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* ───── MODIFIED (from admin) — for original-files purchases
                       this is THE delivered file ("Original file" from
                       customer's POV). ───── */}
              <div
                className={`border rounded-lg p-3 flex flex-col ${order.modified_file_name ? (isOrigFiles ? 'border-blue-300 bg-blue-50/30' : 'border-emerald-300 bg-emerald-50/30') : 'border-slate-200'}`}
                data-testid="file-modified-card"
              >
                <div className="flex items-center justify-between mb-1.5 gap-2">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                    {isOrigFiles ? tt('drawer.original_from_operator', 'Original (from operator)') : tt('drawer.modified_file', 'Modified file')}
                  </div>
                  <div className="flex items-center gap-1.5">
                    {isAdmin && order.modified_file_name && (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 bg-cyan-100 text-cyan-700 rounded-full"
                        title="Times the customer downloaded the file"
                        data-testid="modified-download-count"
                      >
                        <Download className="w-2.5 h-2.5" />
                        {order.modified_download_count || 0}
                      </span>
                    )}
                    {/* Persistent Complete button — visible in the card header
                        for ANY admin-visible status that's still actionable
                        (not completed/cancelled/closed). Lives in the header
                        so it stays reachable even when the order is overdue,
                        ready_at is set in the future (admin scheduled
                        delivery), the file was attached via chat, or the
                        panel still renders the empty-state because
                        onChanged() hasn't re-fetched yet. Regression report
                        2026-06-24 — admin couldn't force-complete an order
                        scheduled via "Set custom time" without scrolling
                        around the panel. Styled as a clear button (NOT a
                        small status-looking pill) so it's recognised. */}
                    {isAdmin && !['completed', 'cancelled', 'closed'].includes(order.status) && (
                      <button
                        onClick={markCompleted}
                        data-testid="mark-completed-header-btn"
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold rounded-md bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm transition-colors"
                        title="Force-complete this order now (settle payment, send 'file ready' email, make the modified file downloadable for the customer). Bypasses any scheduled ready_at."
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        <span>Complete</span>
                      </button>
                    )}
                  </div>
                </div>

                {/* Admin Ready-at editor — relocated from the dense
                    timeline strip in the header so engineers can set or
                    adjust the customer-visible delivery time right next
                    to the Modified file action they're about to perform.
                    No scrolling required. */}
                {isAdmin && (
                  <div
                    className="mb-2 px-2 py-1.5 rounded bg-cyan-50/60 border border-cyan-100"
                    data-testid="modified-ready-at-row"
                  >
                    <ReadyAtRow
                      order={order}
                      token={token}
                      isAdmin={isAdmin}
                      onChanged={onChanged}
                      compact
                    />
                  </div>
                )}

                {/* Driver filename hint (original-files admin) */}
                {isOrigFiles && isAdmin && order.original_files_meta?.driver_filename && (
                  <div className="mb-2 px-2 py-1.5 rounded bg-amber-50 border border-amber-200 text-[11px] text-amber-900">
                    <span className="font-bold">Driver file:</span>{' '}
                    <code className="bg-white/60 px-1 rounded">{order.original_files_meta.driver_filename}</code>
                    <div className="text-[10px] text-amber-700 mt-0.5">↳ search this on alientech.</div>
                  </div>
                )}

                {(order.modified_file_name || order.modified_file_locked) ? (
                  (() => {
                    const isCompletedStatus = order.status === 'completed' || order.status === 'closed';
                    const readyAtPassed = order.ready_at && new Date(order.ready_at).getTime() <= Date.now();
                    // `modified_file_locked` from backend means: file
                    // exists but customer can't see it yet (name/size
                    // scrubbed). Treat as locked-for-customer regardless
                    // of any client-side status guess.
                    const locked = order.modified_file_locked || (!isAdmin && !isCompletedStatus && !readyAtPassed);
                    const readyTimeStr = fmt(order.ready_at).split(' ')[1] || fmt(order.ready_at);
                    const lockReason = !isAdmin && !isCompletedStatus && order.ready_at && !readyAtPassed
                      ? (isAdmin ? `Will be ready at ${readyTimeStr}` : t('drawer.ready_at_time', { time: readyTimeStr, defaultValue: `Will be ready at ${readyTimeStr}` }))
                      : tt('drawer.awaiting_approval', 'Awaiting engineer approval');
                    // Customer-facing "locked" mode: HIDE the filename +
                    // size + kebab menu entirely so the client cannot
                    // see a name they cannot download yet (msg-1218 —
                    // users kept trying to click it and pinging support).
                    // Show a neutral placeholder card instead — same
                    // spot, same border colour, no filename leak.
                    if (locked) {
                      return (
                        <div className="flex flex-col items-center justify-center py-4 px-3 text-center">
                          <Clock className="w-8 h-8 text-emerald-500/70 mb-2" />
                          <div className="text-sm font-semibold text-slate-800 mb-0.5">
                            {t('drawer.file_being_prepared_title', {
                              defaultValue: 'Your tuned file is being prepared',
                            })}
                          </div>
                          <div className="text-xs text-slate-500 leading-snug">
                            {order.ready_at && !readyAtPassed
                              ? t('drawer.ready_at_time', {
                                  time: readyTimeStr,
                                  defaultValue: `Will be ready at ${readyTimeStr}`,
                                })
                              : t('drawer.awaiting_approval', {
                                  defaultValue: 'Awaiting engineer approval',
                                })}
                          </div>
                          <div className="mt-3 w-full h-1 bg-emerald-100 rounded-full overflow-hidden">
                            <div className="h-full w-1/2 bg-gradient-to-r from-emerald-400 to-emerald-500 animate-pulse" />
                          </div>
                        </div>
                      );
                    }
                    return (
                      <>
                        <div className="text-sm font-semibold text-slate-900 break-all leading-snug" title={order.modified_file_name}>
                          {order.modified_file_name}
                        </div>
                        <div className="text-xs text-slate-500 mb-1 tabular-nums">{formatBytes(order.modified_file_size)}</div>
                        {isAdmin && (
                          <div className="text-[10px] text-emerald-700 mb-2 leading-snug">
                            Uploaded {fmt(order.modified_uploaded_at)}
                            {(order.modified_replaced_count || 0) > 0 && (
                              <span className="ml-1.5 text-amber-700">· replaced {order.modified_replaced_count}×</span>
                            )}
                          </div>
                        )}

                        <div className="mt-auto flex flex-wrap gap-1.5" data-testid="modified-actions-row">
                          <button
                            onClick={() => downloadFile('modified')}
                            data-testid="download-modified-btn"
                            className={`${ACTION_PRIMARY} flex-1 min-w-[110px] ${
                              isOrigFiles
                                ? 'bg-gradient-to-r from-blue-500 via-cyan-500 to-teal-500 hover:shadow-md'
                                : 'bg-gradient-to-r from-emerald-500 to-emerald-600 hover:shadow-md'
                            }`}
                          >
                            <Download className="w-3.5 h-3.5" />
                            {tt('drawer.download', 'Download')}
                          </button>
                          {/* Complete action has moved to the card header
                              (data-testid="mark-completed-header-btn") so
                              there's a single source-of-truth button per
                              card. Keeping two was confusing (msg-1043). */}
                          {/* Kebab menu (3-dots) — hosts the rarely-used Replace
                              action so the primary row (Download / Complete) stays
                              compact. Admin only. The actual file input is still
                              a <label htmlFor=…> wrapper around the menu item — the
                              `replace-modified-input` lives outside the DropdownMenu
                              so it survives the menu closing on click. */}
                          {isAdmin && (
                            <>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <button
                                    type="button"
                                    data-testid="modified-actions-menu"
                                    title="More actions"
                                    className={`${ACTION_SECONDARY} px-2 border-slate-300 bg-white text-slate-600 hover:bg-slate-50 ${
                                      uploading ? 'cursor-wait opacity-60' : ''
                                    }`}
                                    disabled={uploading}
                                  >
                                    {uploading
                                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                      : <MoreVertical className="w-3.5 h-3.5" />}
                                  </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-44">
                                  <DropdownMenuItem
                                    onSelect={(e) => {
                                      // Bridge the menu-item click to the hidden file
                                      // input below so the standard browser file
                                      // picker opens — same flow as before, just
                                      // tucked behind one extra click.
                                      e.preventDefault();
                                      document.getElementById('replace-modified-input')?.click();
                                    }}
                                    data-testid="replace-modified-menuitem"
                                    className="cursor-pointer text-amber-800 focus:bg-amber-50 focus:text-amber-900"
                                  >
                                    <Upload className="w-3.5 h-3.5 mr-2" />
                                    Replace file
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                              <input
                                id="replace-modified-input"
                                type="file"
                                className="hidden"
                                onChange={uploadModified}
                                disabled={uploading}
                                data-testid="replace-modified-input"
                              />
                            </>
                          )}
                        </div>
                      </>
                    );
                  })()
                ) : isAdmin ? (
                  <>
                    {order.legacy_order_id && (
                      <div className="mb-3">
                        <LegacyFileDownloadButton
                          token={token}
                          orderId={order.id}
                          kind="modified"
                          label="Download modified (legacy)"
                          hintPath={order.legacy_file_modified_path}
                          testid="download-legacy-modified-btn"
                        />
                        <p className="text-[10px] text-slate-500 mt-1 italic">
                          Served from migrated local storage. 404 = old system never produced a modified file.
                        </p>
                      </div>
                    )}
                    <p className="text-xs text-slate-500 italic mb-2">
                      {isOrigFiles
                        ? 'Upload the original file you obtained from alientech (or your archive).'
                        : 'Upload the modified file to deliver to customer.'}
                    </p>
                    <label className={`${ACTION_PRIMARY} cursor-pointer ${
                      uploading
                        ? 'bg-slate-300'
                        : isOrigFiles
                          ? 'bg-gradient-to-r from-blue-500 via-cyan-500 to-teal-500 hover:shadow-md'
                          : 'bg-gradient-to-r from-cyan-500 to-blue-600 hover:shadow-md'
                    }`} data-testid="upload-modified-label">
                      {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                      {uploading ? 'Uploading…' : (isOrigFiles ? 'Upload original' : 'Upload modified')}
                      <input type="file" className="hidden" onChange={uploadModified} disabled={uploading} data-testid="upload-modified-input" />
                    </label>
                  </>
                ) : order.legacy_order_id ? (
                  <LegacyFileDownloadButton
                    token={token}
                    orderId={order.id}
                    kind="modified"
                    label={tt('drawer.download_modified', 'Download modified')}
                    testid="download-legacy-modified-btn-customer"
                  />
                ) : (
                  <p className="text-xs text-amber-600 italic">⏳ {tt('drawer.awaiting_file', 'Awaiting file from operator')}</p>
                )}

                {/* Attachments (WinOLS .ini) used to live here, under the
                    Modified file column. It was relocated into the Original
                    file column on the left, which has far fewer action
                    buttons (especially after a completed order) and so
                    handles the extra row without growing taller. */}
              </div>
            </div>
          </section>

          {/* Instructions — full editor card for ADMIN ONLY (textarea +
              file attachments). The customer-facing strip is already
              rendered above next to "Description from customer" inside
              the combined "customer-context-row", so we don't render
              the bottom InstructionsBlock for customers. */}
          {isAdmin && (
            <InstructionsBlock
              token={token}
              order={order}
              isAdmin
              isCustomer={false}
            />
          )}
          </>
          );})()}
          </div>
          </div>
          </div>

          {/* RIGHT: ticket / chat panel (7 cols on desktop — wider for
              conversation, pairs with 5-col details on the left). */}
          <div className={`flex flex-col bg-white border-l border-slate-200 overflow-hidden lg:col-span-7 min-h-0 ${mobileTab !== 'chat' ? 'hidden lg:flex' : ''}`}>
            <div className="px-5 py-4 border-b border-slate-200 bg-gradient-to-r from-slate-50 via-cyan-50/40 to-blue-50/40 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-md flex-shrink-0">
                <MessageSquare className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                  {tt('drawer.ticket_conversation', 'Ticket Conversation')}
                  {isAdmin && legacyTicket && (
                    <span
                      className="text-[9px] px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 border border-violet-300 font-bold uppercase tracking-wider"
                      title="This order has imported PHP conversation"
                      data-testid="conversation-legacy-badge"
                    >
                      Legacy imported
                    </span>
                  )}
                </h3>
                <p className="text-xs text-slate-500">
                  {tt('drawer.with', 'with')} {(() => {
                    const label = order.customer_company || [order.customer_first_name, order.customer_surname].filter(Boolean).join(' ') || order.user_email;
                    if (isAdmin && order.user_id) {
                      return (
                        <button
                          onClick={() => setOpenCustomerId(order.user_id)}
                          data-testid="order-drawer-customer-link"
                          className="font-semibold text-cyan-700 hover:text-cyan-900 underline decoration-dotted"
                          title="Open customer profile"
                        >
                          {label}
                        </button>
                      );
                    }
                    return <span className="font-semibold text-cyan-700">{label}</span>;
                  })()}
                </p>
              </div>

              {/* Conversation summary chips (Phase 3): Messages · Files ·
                  Last reply ago. Gives engineers an at-a-glance read of
                  ticket activity before scrolling. */}
              {(() => {
                const allMsgs = [...legacyMessages, ...messages];
                const total = allMsgs.length;
                if (total === 0) return null;
                const fileCount = allMsgs.filter(
                  (mm) => !!mm.attachment || !!mm.legacy_attachment_path,
                ).length;
                const lastAt = [...allMsgs].reverse().find((mm) => mm.created_at)?.created_at;
                let agoStr = null;
                if (lastAt) {
                  const diff = Math.max(0, Date.now() - new Date(lastAt).getTime());
                  const m = Math.floor(diff / 60000);
                  if (m < 1) agoStr = tt('drawer.just_now', 'just now');
                  else if (m < 60) agoStr = isAdmin ? `${m} min ago` : t('drawer.min_ago', { count: m, defaultValue: `${m} min ago` });
                  else if (m < 60 * 24) {
                    const h = Math.floor(m / 60);
                    agoStr = isAdmin ? `${h} h ago` : t('drawer.hours_ago', { count: h, defaultValue: `${h} h ago` });
                  }
                  else {
                    const d = Math.floor(m / (60 * 24));
                    agoStr = isAdmin ? `${d} d ago` : t('drawer.days_ago', { count: d, defaultValue: `${d} d ago` });
                  }
                }
                return (
                  <div
                    className="hidden sm:flex items-center gap-1.5 flex-shrink-0"
                    data-testid="ticket-conversation-summary"
                  >
                    <span
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white border border-slate-200 text-[11px] text-slate-700 font-semibold"
                      data-testid="ticket-summary-messages"
                      title={`${total} message${total === 1 ? '' : 's'}`}
                    >
                      <MessageSquare className="w-3 h-3 text-cyan-600" />
                      {total}
                    </span>
                    {fileCount > 0 && (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white border border-slate-200 text-[11px] text-slate-700 font-semibold"
                        data-testid="ticket-summary-files"
                        title={`${fileCount} file${fileCount === 1 ? '' : 's'} attached`}
                      >
                        <FileText className="w-3 h-3 text-rose-600" />
                        {fileCount}
                      </span>
                    )}
                    {agoStr && (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white border border-slate-200 text-[11px] text-slate-500"
                        data-testid="ticket-summary-last-reply"
                        title={`Last reply at ${fmt(lastAt)}`}
                      >
                        <Clock className="w-3 h-3 text-slate-400" />
                        {agoStr}
                      </span>
                    )}
                  </div>
                );
              })()}
            </div>

            <ResponseEstimateBanner isAdmin={isAdmin} token={token} />

            <div ref={messagesEndRef} className="flex-1 overflow-y-auto p-4 space-y-3" style={{ background: '#f2f5f3' }} data-testid="messages-list">
              {legacyMessages.length === 0 && messages.length === 0 ? (
                <div className="text-center py-10 text-slate-400 text-sm">
                  <MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-40" />
                  <p>{tt('drawer.no_messages_yet', 'No messages yet. Start the conversation below.')}</p>
                </div>
              ) : (
                (() => {
                  // Stamp `__isLegacyGroupHead` so the per-bubble Legacy
                  // pill only appears on the FIRST message in each
                  // consecutive legacy run (currently always: one run,
                  // because new and legacy never interleave).
                  const stamped = legacyMessages.map((m, idx) => ({
                    ...m,
                    __isLegacyGroupHead: idx === 0,
                  }));
                  const merged = [
                    ...(legacyMessages.length > 0 ? [{__divider: 'legacy-start', label: isAdmin ? 'Imported legacy conversation' : tt('drawer.earlier_conversation', 'Earlier conversation')}] : []),
                    ...stamped,
                    ...(legacyMessages.length > 0 && messages.length > 0 ? [{__divider: 'new-start', label: tt('drawer.new_messages', 'New messages')}] : []),
                    ...messages,
                  ];
                  // Insert a date-divider before each message whose calendar
                  // day differs from the previous one. Skipped while the
                  // previous element is itself a divider so we don't stack.
                  const withDates = [];
                  let lastDay = null;
                  for (const m of merged) {
                    if (m.__divider) { withDates.push(m); continue; }
                    const d = m.created_at ? new Date(m.created_at) : null;
                    const dayKey = d && !Number.isNaN(d.getTime())
                      ? `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}` : null;
                    if (dayKey && dayKey !== lastDay) {
                      withDates.push({ __dateDivider: dayKey, __dateDividerIso: m.created_at, label: dateLabel(m.created_at, tt) });
                      lastDay = dayKey;
                    }
                    withDates.push(m);
                  }
                  return withDates;
                })().map((m, mIdx, allItems) => {
                  if (m.__dateDivider) {
                    return (
                      <DateDivider key={`date-${m.__dateDivider}-${mIdx}`} iso={m.__dateDividerIso || undefined} testid={`chat-date-divider-${m.__dateDivider}`} />
                    );
                  }
                  if (m.__divider) {
                    return (
                      <div key={m.__divider} className="flex items-center gap-2 my-2" data-testid={`chat-divider-${m.__divider}`}>
                        <div className="flex-1 h-px bg-slate-200" />
                        <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ${m.__divider === 'legacy-start' ? 'bg-violet-50 text-violet-700 border border-violet-200' : 'bg-cyan-100 text-cyan-700'}`}>
                          {m.label}
                        </span>
                        <div className="flex-1 h-px bg-slate-200" />
                      </div>
                    );
                  }
                  // Find the previous NON-divider message for author-series
                  // compression + read-status derivation.
                  let prevM = null;
                  for (let i = mIdx - 1; i >= 0; i -= 1) {
                    const c = allItems[i];
                    if (c && !c.__dateDivider && !c.__divider) { prevM = c; break; }
                  }
                  const fromAdmin   = m.sender_role === 'admin';
                  const isSystem    = !!m.is_system || m.sender_role === 'bot';
                  const isLegacy    = !!m.is_legacy;
                  const isInternal  = !!m.is_internal;     // admin-only note
                  const isOwn       = !isLegacy && currentUserId && m.user_id === currentUserId;
                  const isEditing   = !isLegacy && editingId === m.id;
                  // ── Layout rule (classic chat) ──────────────────────────
                  //   System / bot     → centered narrow capsule
                  //   Customer (user)  → LEFT  · white/gray bubble
                  //   Admin/engineer   → RIGHT · cyan→blue bubble
                  //   Admin internal   → RIGHT · amber dashed bubble (admin-only view)
                  const onRight    = fromAdmin && !isSystem;
                  const canDelete  = isAdmin && !isLegacy;
                  const readByRecipient = !isLegacy && isOwn && !isInternal && (fromAdmin ? !!m.read_by_user : !!m.read_by_admin);
                  const att = m.attachment;
                  const downloadedByRecipient = att && (fromAdmin ? !!att.downloaded_by_user : !!att.downloaded_by_admin);
                  const downloadCount = att?.download_count || 0;

                  // ── System / bot row: centered capsule ─────────────────
                  if (isSystem) {
                    // System-message text is written by the backend in English
                    // ("Order status: Cancelled\nAmount €85.00 refunded...").
                    // Parse known patterns and substitute translated lines on
                    // the customer side; admin keeps the original English.
                    const translateSystem = (raw) => {
                      if (isAdmin || !raw) return raw;
                      return String(raw).split('\n').map((line) => {
                        let mt = line.match(/^Order status:\s*(.+)$/i);
                        if (mt) {
                          const statusEn = mt[1].trim();
                          const statusKey = statusEn.toLowerCase();
                          const statusTr = t(`status.${statusKey}`, { defaultValue: statusEn });
                          return t('drawer.sys_order_status', { status: statusTr, defaultValue: `Order status: ${statusTr}` });
                        }
                        mt = line.match(/^Amount\s+€([\d.,]+)\s+refunded to your internal account\.?$/i);
                        if (mt) {
                          return t('drawer.sys_amount_refunded', { amount: `€${mt[1]}`, defaultValue: `Amount €${mt[1]} refunded to your internal account.` });
                        }
                        mt = line.match(/^Reason:\s*(.+)$/i);
                        if (mt) {
                          return t('drawer.sys_reason', { reason: mt[1], defaultValue: `Reason: ${mt[1]}` });
                        }
                        return line;
                      }).join('\n');
                    };
                    return (
                      <div key={m.id} className="flex justify-center my-1" data-testid={`message-${m.id}`}>
                        <div className="inline-flex items-center gap-2 max-w-[80%] px-3 py-1.5 bg-slate-100 border border-slate-200 rounded-full text-xs text-slate-600 shadow-sm">
                          <span className="font-medium text-slate-500">{m.sender_name || tt('drawer.sys_sender_label', 'System')}</span>
                          <span className="text-slate-400 whitespace-pre-wrap break-words">{translateSystem(m.text)}</span>
                          <span className="text-[10px] text-slate-400 whitespace-nowrap">{fmt(m.created_at)}</span>
                        </div>
                      </div>
                    );
                  }

                  // ── Customer / Admin / Internal-note row ───────────────
                  // File-only message: keep bubble background so the
                  // attachment card sits INSIDE the bubble per spec §80
                  // ("вложение выводится внутри пузыря").
                  const isFileOnly =
                    !m.text && (!!m.attachment || (isLegacy && m.legacy_attachment_path));
                  const avatarBg = isInternal
                    ? 'bg-gradient-to-br from-amber-400 to-amber-600 text-white'
                    : fromAdmin
                      ? 'bg-gradient-to-br from-emerald-500 to-emerald-700 text-white'
                      : 'bg-gradient-to-br from-violet-400 to-violet-600 text-white';
                  // Mint variant B: own = soft mint, other = white +
                  // subtle border. Internal notes stay amber for clear
                  // "not-shared" cue.
                  const bubbleBaseStyle = isInternal
                    ? { background: '#fef3c7', border: '2px dashed #f59e0b' }
                    : onRight
                      ? { background: CHAT_TOKENS.ownBg }
                      : { background: CHAT_TOKENS.otherBg, border: `1px solid ${CHAT_TOKENS.otherBorder}` };
                  const bubbleTextColor = isInternal ? '#78350f' : CHAT_TOKENS.text;
                  const bubbleCorner = onRight
                    ? 'rounded-[12px] rounded-br-[4px]'
                    : 'rounded-[12px] rounded-bl-[4px]';
                  const showAuthorLabel = shouldShowAuthorLabel(prevM, m);
                  return (
                    <React.Fragment key={m.id}>
                    <div
                      className={`group flex gap-2 ${onRight ? 'flex-row-reverse' : 'flex-row'} ${isLegacy ? 'opacity-95' : ''}`}
                      data-testid={`message-${m.id}`}
                    >
                      {/* Avatar (Phase C will swap initials for uploaded photos). */}
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 shadow-sm ${avatarBg}`}
                           title={m.sender_name || m.user_email}
                           data-testid={`chat-avatar-${m.id}`}>
                        {m.sender_avatar_url
                          ? <AuthedAvatarImage token={token} src={m.sender_avatar_url} alt={m.sender_name} className="w-full h-full rounded-full object-cover" fallback={<span>{getInitials(m.sender_name, m.user_email)}</span>} />
                          : getInitials(m.sender_name, m.user_email)}
                      </div>

                      <div className={`max-w-[82%] min-w-0 ${onRight ? 'items-end text-right' : 'items-start text-left'} flex flex-col`}>
                        {/* Sender meta strip — only for series head per
                            spec §66-70; suppressed when the previous
                            message is from the same author within 5 min. */}
                        {showAuthorLabel && (
                          <p className={`mb-0.5 flex items-center gap-1.5 flex-wrap ${onRight ? 'justify-end' : 'justify-start'}`}
                             style={{ fontSize: 11, fontWeight: 500, padding: '0 2px' }}>
                          {isAdmin && !fromAdmin && order.user_id ? (
                            <button
                              type="button"
                              onClick={() => setOpenCustomerId(order.user_id)}
                              data-testid={`chat-sender-${m.id}`}
                              className="hover:underline decoration-dotted"
                              style={{ color: onRight ? CHAT_TOKENS.authorOwn : CHAT_TOKENS.authorOther, fontWeight: 600 }}
                              title="Open customer profile"
                            >
                              {m.sender_name}
                            </button>
                          ) : (
                            <span style={{ color: onRight ? CHAT_TOKENS.authorOwn : CHAT_TOKENS.authorOther, fontWeight: 600 }}>{m.sender_name}</span>
                          )}
                          <span className="text-[10px] uppercase tracking-wider" style={{ color: CHAT_TOKENS.metaFaint, fontWeight: 500 }}>
                            · {fromAdmin ? (isInternal ? 'Admin · Internal' : tt('drawer.role_admin', 'Admin')) : tt('drawer.role_customer', 'Customer')}
                          </span>
                          {isAdmin && isLegacy && m.__isLegacyGroupHead && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded bg-violet-50 text-violet-600 border border-violet-200 font-semibold uppercase">Legacy</span>
                          )}
                          {isAdmin && isLegacy && m.legacy_type_id === 1 && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 border border-violet-200">type 1</span>
                          )}
                          {isInternal && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 border border-amber-300 font-bold uppercase tracking-wider inline-flex items-center gap-1">
                              <Lock className="w-2.5 h-2.5" /> Internal note
                            </span>
                          )}
                        </p>
                        )}

                        {/* Bubble + side action icons */}
                        <div className={`flex items-start gap-1.5 ${onRight ? 'flex-row-reverse' : 'flex-row'}`}>
                          <div
                            className={`relative inline-block text-sm whitespace-pre-wrap break-words ${bubbleCorner}`}
                            style={{
                              ...bubbleBaseStyle,
                              color: bubbleTextColor,
                              padding: '8px 12px 6px',
                              boxShadow: isInternal ? undefined : '0 1px 1px rgba(0,0,0,0.04)',
                            }}
                          >
                          {isEditing ? (
                            <div className="text-left min-w-[220px]">
                              <textarea
                                value={editingText}
                                onChange={(e) => setEditingText(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Escape') { e.preventDefault(); cancelEdit(); }
                                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); saveEdit(m); }
                                }}
                                rows="2"
                                data-testid={`edit-input-${m.id}`}
                                className="w-full px-2 py-1.5 rounded text-sm resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white border border-slate-300 text-slate-800"
                              />
                              <div className="mt-1 flex gap-1 justify-end">
                                <button
                                  type="button"
                                  onClick={cancelEdit}
                                  className="px-2 py-1 rounded text-xs bg-slate-100 text-slate-700 hover:bg-slate-200"
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  onClick={() => saveEdit(m)}
                                  data-testid={`save-edit-${m.id}`}
                                  className="px-2 py-1 rounded text-xs bg-emerald-500 hover:bg-emerald-600 text-white inline-flex items-center gap-1"
                                >
                                  <Check className="w-3 h-3" /> Save
                                </button>
                              </div>
                            </div>
                          ) : (
                            <>
                              {m.text}
                              {/* Translation result inline */}
                              {translations[m.id] && (
                                <div className="mt-1.5 pt-1.5 border-t border-slate-200 text-slate-600 text-[12px] italic"
                                  data-testid={`translation-${m.id}`}>
                                  <span className="font-semibold not-italic mr-1">→ {translations[m.id].lang}:</span>
                                  {translations[m.id].translated}
                                </div>
                              )}
                              {/* Translate button — visible on incoming messages only when AI enabled */}
                              {!isOwn && aiSettings?.enabled && m.text && (m.sender_role !== 'bot') && (
                                <div className="relative inline-block ml-1.5 align-middle">
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      if (translateMenu === m.id) {
                                        setTranslateMenu(null);
                                        setTranslateMenuPos(null);
                                        return;
                                      }
                                      const rect = e.currentTarget.getBoundingClientRect();
                                      setTranslateMenuPos({
                                        top: rect.bottom + 4,
                                        left: rect.left,
                                      });
                                      setTranslateMenu(m.id);
                                    }}
                                    data-testid={`translate-btn-${m.id}`}
                                    title="Translate this message"
                                    className="inline-flex items-center justify-center w-6 h-6 rounded bg-slate-200 text-slate-700 hover:bg-slate-300"
                                  >
                                    {translating === m.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Languages className="w-3 h-3" />}
                                  </button>
                                  {translateMenu === m.id && translateMenuPos && aiSettings && createPortal(
                                    <div
                                      className="fixed z-[9999] bg-white shadow-2xl rounded-lg border border-slate-200 py-1 min-w-[180px] max-h-72 overflow-y-auto"
                                      style={{ top: translateMenuPos.top, left: translateMenuPos.left }}
                                      data-testid={`translate-menu-${m.id}`}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {(aiSettings.available_languages || []).map(l => (
                                        <button key={l.code} type="button"
                                          onClick={() => { aiTranslate(m, l.code); setTranslateMenuPos(null); }}
                                          data-testid={`translate-lang-${m.id}-${l.code}`}
                                          className="block w-full text-left px-3 py-1.5 text-xs hover:bg-slate-100 text-slate-700">
                                          <span className="font-mono text-slate-400 mr-1.5">{l.code}</span>
                                          {l.name}
                                        </button>
                                      ))}
                                    </div>,
                                    document.body
                                  )}
                                </div>
                              )}
                            </>
                          )}
                          {/* Attachments — INSIDE bubble per mint spec §80.
                              Card is neutral white so bubble tint does not
                              bleed into it. */}
                          {!isEditing && (m.attachment || (isLegacy && m.legacy_attachment_path)) && (
                            <div className="flex flex-col gap-1.5" data-testid={`attachment-slot-${m.id}`}>
                              {isLegacy && m.legacy_attachment_path && (
                                <AttachmentCard
                                  filename={isAdmin ? m.legacy_attachment_path : (m.legacy_attachment_path || '').split('/').pop()}
                                  fullTitle={isAdmin ? m.legacy_attachment_path : ((m.legacy_attachment_path || '').split('/').pop() || 'attachment')}
                                  ext={((m.legacy_attachment_path || '').split('.').pop() || '').toLowerCase()}
                                  uploadedAt={m.created_at}
                                  legacy
                                  downloadCount={isAdmin ? (m.legacy_downloads ?? 0) : null}
                                  onCopyPath={isAdmin ? () => {
                                    navigator.clipboard?.writeText(m.legacy_attachment_path);
                                    toast.success('Legacy path copied');
                                  } : null}
                                  downloadDisabled={legacyDownloading.has(m.legacy_message_id)}
                                  onDownload={async () => {
                                    const mid = m.legacy_message_id;
                                    if (!mid) return;
                                    setLegacyDownloading(prev => { const s = new Set(prev); s.add(mid); return s; });
                                    try {
                                      const r = await fetch(
                                        `${API_URL}/api/legacy-files/file_message/${encodeURIComponent(mid)}`,
                                        { headers: { Authorization: `Bearer ${token}` } },
                                      );
                                      if (!r.ok) {
                                        const j = await r.json().catch(() => ({}));
                                        const detail = (typeof j?.detail === 'object' ? j.detail : null) || {};
                                        if (detail.code === 'legacy_file_missing' || r.status === 404) toast.error('Attachment unavailable.');
                                        else if (detail.code === 'forbidden' || r.status === 403) toast.error('This attachment does not belong to your account.');
                                        else toast.error(`Download failed: ${detail.message || j?.detail || `HTTP ${r.status}`}`);
                                        return;
                                      }
                                      const blob = await r.blob();
                                      const ct = (r.headers.get('content-type') || '').toLowerCase();
                                      if (ct.startsWith('text/') || ct === 'application/json' || ct === 'application/xml') {
                                        toast.error('Legacy attachment is temporarily unavailable. Please retry in a moment.');
                                        return;
                                      }
                                      const cd = r.headers.get('content-disposition') || '';
                                      const mm = cd.match(/filename\*=UTF-8''([^;]+)/i) || cd.match(/filename="?([^";]+)"?/i);
                                      const name = mm ? decodeURIComponent(mm[1]) : (m.legacy_attachment_path.split('/').pop() || `attachment-${mid}`);
                                      const url = URL.createObjectURL(blob);
                                      const a = document.createElement('a');
                                      a.href = url; a.download = name;
                                      document.body.appendChild(a); a.click(); a.remove();
                                      setTimeout(() => URL.revokeObjectURL(url), 1000);
                                      toast.success(`Downloaded ${name}`);
                                    } catch (err) { toast.error(`Download failed: ${err?.message || err}`); }
                                    finally { setLegacyDownloading(prev => { const s = new Set(prev); s.delete(mid); return s; }); }
                                  }}
                                  testid={`legacy-msg-download-btn-${m.legacy_message_id}`}
                                />
                              )}
                              {m.attachment && (
                                <AttachmentCard
                                  filename={m.attachment.name}
                                  fullTitle={m.attachment.name}
                                  size={m.attachment.size}
                                  ext={m.attachment.ext}
                                  uploadedAt={m.created_at}
                                  downloadCount={downloadCount}
                                  imageUrl={m.attachment.is_image ? imageUrls[m.id] : null}
                                  onOpenImage={m.attachment.is_image ? () => imageUrls[m.id] && setLightbox({ url: imageUrls[m.id], name: m.attachment.name, message: m }) : null}
                                  onPreviewPdf={(m.attachment.ext || '').toLowerCase() === 'pdf' ? () => setPdfPreview(m) : null}
                                  onDownload={() => downloadAttachment(m)}
                                  testid={`chat-attachment-${m.id}`}
                                />
                              )}
                            </div>
                          )}
                          {/* Inline meta — HH:MM · ✓✓ inside bottom-right
                              of bubble per spec §56-64. */}
                          {!isEditing && (
                            <div
                              className={`flex items-center gap-1 mt-0.5 ${onRight ? 'justify-end' : 'justify-start'}`}
                              style={{ fontSize: 10, color: CHAT_TOKENS.meta, marginRight: onRight ? -2 : 0 }}
                            >
                              <span className="font-mono">{formatBubbleTime(m.created_at)}</span>
                              {m.edited_at && <span className="italic">· edited</span>}
                              {isOwn && !isInternal && (() => {
                                const status = deriveReadStatus({
                                  ...m,
                                  read_by_recipient: fromAdmin ? m.read_by_user : m.read_by_admin,
                                });
                                if (status === 'read') {
                                  return <CheckCheck className="w-3 h-3" style={{ color: CHAT_TOKENS.readActive }} data-testid={`read-status-${m.id}`} data-status="read" />;
                                }
                                if (status === 'delivered') {
                                  return <CheckCheck className="w-3 h-3" style={{ color: CHAT_TOKENS.metaFaint }} data-testid={`read-status-${m.id}`} data-status="delivered" />;
                                }
                                return <Check className="w-3 h-3" style={{ color: CHAT_TOKENS.metaFaint }} data-testid={`read-status-${m.id}`} data-status="sent" />;
                              })()}
                            </div>
                          )}
                        </div>
                        {/* Side action icons (edit / delete) */}
                        {!isEditing && (isOwn || canDelete) && (
                          <div className="flex items-center gap-0.5 self-center opacity-50 group-hover:opacity-100 transition-opacity">
                            {isOwn && (
                              <button type="button" onClick={() => startEdit(m)}
                                data-testid={`edit-message-${m.id}`} title="Edit"
                                className="p-1 text-slate-400 hover:text-cyan-600 hover:bg-slate-100 rounded">
                                <Pencil className="w-3 h-3" />
                              </button>
                            )}
                            {canDelete && (
                              <button type="button" onClick={() => deleteMessage(m)}
                                data-testid={`delete-message-${m.id}`} title="Delete"
                                className="p-1 text-slate-400 hover:text-red-600 hover:bg-slate-100 rounded">
                                <Trash2 className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        )}
                        </div>
                      </div>
                    </div>
                    </React.Fragment>
                  );
                })
              )}
            </div>

            {/* Typing indicator — sits just above the composer. Uses the
                `ticket:typing` broadcast so no extra HTTP traffic. */}
            {peerTyping && (
              <div data-testid="typing-indicator" className="px-4 py-1.5 bg-slate-50 border-t border-slate-100 text-[11px] text-slate-500 flex items-center gap-2 flex-shrink-0">
                <span className="inline-flex gap-0.5" aria-hidden>
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce [animation-delay:-0.3s]" />
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce [animation-delay:-0.15s]" />
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" />
                </span>
                <span className="font-medium">
                  {peerTyping.sender_role === 'admin' ? tt('drawer.typing_support', 'Support') : (peerTyping.sender_name || tt('drawer.role_customer', 'Customer'))} {tt('drawer.is_typing', 'is typing…')}
                </span>
              </div>
            )}

            <div className="p-3 md:p-4 border-t border-slate-200 bg-white flex-shrink-0">
              {warrantyInfo.expired ? (
                <div
                  data-testid="warranty-expired-banner"
                  className="md:max-w-[80%] md:ml-12 md:mr-auto rounded-lg border border-amber-300 bg-gradient-to-br from-amber-50 to-yellow-50 px-4 py-3 text-sm text-amber-900 flex items-start gap-3"
                >
                  <Clock className="w-5 h-5 flex-shrink-0 text-amber-600 mt-0.5" />
                  <div className="min-w-0">
                    {warrantyMsgHtml ? (
                      <SafeHtml as="div" className="ticket-rich-text" html={warrantyMsgHtml} />
                    ) : (
                      <p>
                        {isAdmin
                          ? <>Support for this order not available cause of warranty period for the created order has expired (1 year from the moment the order was created):{' '}<strong>{warrantyInfo.createdLabel}</strong></>
                          : <>{t('drawer.warranty_expired', { date: warrantyInfo.createdLabel, defaultValue: `Support for this order not available cause of warranty period for the created order has expired (1 year from the moment the order was created): ${warrantyInfo.createdLabel}` })}</>
                        }
                      </p>
                    )}
                  </div>
                </div>
              ) : (
              /* Composer — full width on mobile, narrower on desktop so the
                  Send button doesn't land at the extreme right edge. */
              <div className="md:max-w-[80%] md:ml-12 md:mr-auto">
              {/* AI improve suggestion box (admin only) */}
              {isAdmin && aiSuggestion && (
                <div className="mb-2 p-3 bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-lg" data-testid="ai-suggestion-box">
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <div className="flex items-center gap-1.5 text-xs font-bold text-blue-700">
                      <Sparkles className="w-3.5 h-3.5" /> AI improved version
                    </div>
                    <button onClick={() => setAiSuggestion(null)}
                      className="text-slate-400 hover:text-slate-700">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="text-sm text-slate-800 mb-2 whitespace-pre-line">{aiSuggestion}</p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => { setDraft(aiSuggestion); setAiSuggestion(null); }}
                      data-testid="ai-suggestion-accept"
                      className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white rounded text-xs font-bold flex items-center gap-1">
                      <Check className="w-3 h-3" /> Use this
                    </button>
                    <button
                      onClick={() => setAiSuggestion(null)}
                      className="px-3 py-1 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 rounded text-xs">
                      Discard
                    </button>
                  </div>
                </div>
              )}
              {attachment && (
                <div className="mb-2 flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-cyan-50 to-blue-50 rounded-lg border border-cyan-200 shadow-sm">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center flex-shrink-0">
                    {attachment.type?.startsWith('image/') ? <ImageIcon className="w-4 h-4 text-white" /> : <Paperclip className="w-4 h-4 text-white" />}
                  </div>
                  <span className="text-xs text-slate-700 flex-1 truncate font-medium" title={attachment.name}>{attachment.name}</span>
                  <span className="text-[10px] text-slate-500 font-mono">{formatBytes(attachment.size)}</span>
                  <button onClick={() => setAttachment(null)} className="text-slate-500 hover:text-red-600 p-1 rounded hover:bg-white"><X className="w-4 h-4" /></button>
                </div>
              )}
              <div className="flex gap-2 items-end">
                <label className="self-end p-2.5 cursor-pointer text-slate-500 hover:text-cyan-600 hover:bg-cyan-50 rounded-lg transition-colors" title={tt('drawer.attach_file_or_photo', 'Attach file or photo')}>
                  <Paperclip className="w-5 h-5" />
                  <input type="file" className="hidden" onChange={(e) => setAttachment(e.target.files?.[0] || null)} data-testid="attach-file-input" />
                </label>
                {/* Internal-note toggle — admin/engineer only */}
                {isAdmin && (
                  <button
                    type="button"
                    onClick={() => setInternalNote(v => !v)}
                    data-testid="internal-note-toggle"
                    title={internalNote ? 'Internal note ON — customer will NOT see this message' : 'Mark as internal note (only admins/engineers see it)'}
                    className={`self-end p-2.5 rounded-lg transition-colors inline-flex items-center gap-1.5 ${internalNote ? 'bg-amber-100 text-amber-700 border border-amber-300' : 'text-slate-500 hover:bg-amber-50 hover:text-amber-700'}`}
                  >
                    <Lock className="w-5 h-5" />
                    <span className="hidden md:inline text-xs font-semibold">{internalNote ? 'Internal' : 'Internal note'}</span>
                  </button>
                )}
                {/* AI Improve button — admin only */}
                {isAdmin && aiSettings?.enabled && (
                  <button
                    onClick={aiImprove}
                    disabled={improving || !draft.trim()}
                    data-testid="ai-improve-btn"
                    title="Improve text with AI (polish English)"
                    className="self-end p-2.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-40">
                    {improving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
                  </button>
                )}
                <textarea
                  value={draft}
                  onChange={(e) => { setDraft(e.target.value); notifyTyping(); }}
                  onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); sendMessage(); } }}
                  placeholder={isAdmin && internalNote
                    ? 'Internal note — not visible to the customer…  (Ctrl+Enter to send)'
                    : tt('drawer.type_a_message', 'Type a message…  (Ctrl+Enter to send)')}
                  data-testid="message-input"
                  rows="2"
                  className={`flex-1 px-3 py-2 border rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 resize-none ${
                    isAdmin && internalNote
                      ? 'border-amber-300 focus:ring-amber-500 bg-amber-50/40'
                      : 'border-slate-300 focus:ring-cyan-500'
                  }`}
                  disabled={sending}
                />
                <button
                  onClick={sendMessage}
                  disabled={sending || (!draft.trim() && !attachment)}
                  data-testid="send-message-btn"
                  className={`px-3 md:px-5 py-2.5 text-white rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg md:hover:scale-105 transition-all flex items-center gap-2 self-end ${
                    isAdmin && internalNote
                      ? 'bg-gradient-to-r from-amber-500 to-amber-600'
                      : 'bg-gradient-to-r from-cyan-500 to-blue-600'
                  }`}
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  <span className="hidden sm:inline">{isAdmin && internalNote ? 'Save note' : tt('drawer.send', 'Send')}</span>
                </button>
              </div>
              </div>
              )}
            </div>
          </div>
        </div>

        {/* Customer-note photo / PDF preview — image lightbox or PDF iframe */}
        {notePreview && (
          <div
            className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4"
            onClick={() => setNotePreview(null)}
            data-testid="note-preview-overlay"
          >
            <button
              onClick={() => setNotePreview(null)}
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
              data-testid="note-preview-close"
              aria-label="Close preview"
            >
              <X className="w-6 h-6" />
            </button>
            <a
              href={notePreview.blobUrl}
              download={notePreview.name}
              onClick={(e) => e.stopPropagation()}
              className="absolute top-4 right-20 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center gap-2 text-sm font-medium"
              data-testid="note-preview-download"
            >
              <Download className="w-4 h-4" /> Download
            </a>
            {notePreview.kind === 'pdf' ? (
              <iframe
                src={notePreview.blobUrl}
                title={notePreview.name}
                className="w-full max-w-5xl h-[88vh] bg-white rounded-lg shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <img
                src={notePreview.blobUrl}
                alt={notePreview.name}
                className="max-w-full max-h-full object-contain"
                onClick={(e) => e.stopPropagation()}
              />
            )}
            <p className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/80 text-sm font-medium px-3 py-1 bg-black/40 rounded-full">
              {notePreview.name}
            </p>
          </div>
        )}

        {/* Image lightbox */}
        {lightbox && (
          <div className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4" onClick={() => setLightbox(null)} data-testid="image-lightbox">
            <button onClick={() => setLightbox(null)} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"><X className="w-6 h-6" /></button>
            {lightbox.message && (
              <button
                onClick={(e) => { e.stopPropagation(); downloadAttachment(lightbox.message); }}
                data-testid="lightbox-download-btn"
                className="absolute top-4 right-20 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center gap-2 text-sm font-medium"
              >
                <Download className="w-4 h-4" /> Download
              </button>
            )}
            <img src={lightbox.url} alt={lightbox.name} className="max-w-full max-h-full object-contain" onClick={(e) => e.stopPropagation()} />
            <p className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/80 text-sm font-medium px-3 py-1 bg-black/40 rounded-full">{lightbox.name}</p>
          </div>
        )}
        {/* PDF preview modal */}
        {pdfPreview && (
          <PdfPreview
            orderId={order.id}
            message={pdfPreview}
            token={token}
            onClose={() => setPdfPreview(null)}
          />
        )}
        {/* Customer profile drawer — opens when admin clicks a customer name */}
        {isAdmin && openCustomerId && (
          <CustomerProfileDrawer
            token={token}
            customerId={openCustomerId}
            onClose={() => setOpenCustomerId(null)}
          />
        )}
      </div>
    </div>
  );
}



/**
 * Visible on imported orders. Shows legacy_order_id, status, transaction
 * link, and the two preserved file paths. If `legacy_file_base_url` is
 * configured in app_settings, the paths become clickable links; otherwise
 * we show "file storage not connected yet" so the admin can't accidentally
 * 404 their way out of the page.
 */
function LegacyOrderBlock({ order, token, isAdmin }) {
  const [baseUrl, setBaseUrl] = React.useState('');
  const [downloading, setDownloading] = React.useState(null); // 'file_default' | 'file_modified' | null
  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/admin/import/legacy/settings`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (!r.ok) return;
        const j = await r.json();
        if (alive) setBaseUrl((j.legacy_file_base_url || '').replace(/\/$/, ''));
      } catch (_) { /* customers don't have permission — silent */ }
    })();
    return () => { alive = false; };
  }, [token]);

  /**
   * Download the binary from the old site via our backend proxy. The
   * shared `/api/legacy-files/{type}/{id}` endpoint enforces ownership
   * server-side — admins see anything; customers only their own.
   * Keys never leave the backend.
   */
  const downloadViaProxy = React.useCallback(async (kind) => {
    if (!order?.id) return;
    setDownloading(kind);
    try {
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/orders/`
        + `${encodeURIComponent(order.id)}/legacy-file/${kind}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        const detail = (typeof j?.detail === 'object' ? j.detail : null) || {};
        const code = detail.code;
        const msg = detail.message || j?.detail || `HTTP ${r.status}`;
        if (code === 'legacy_file_missing' || code === 'no_file_recorded' || r.status === 404) {
          toast.error('Legacy file unavailable — not recorded for this order in the old system.');
        } else if (code === 'forbidden' || r.status === 403) {
          toast.error('This order does not belong to your account.');
        } else {
          toast.error(`Legacy download failed: ${msg}`);
        }
        return;
      }
      const blob = await r.blob();
      const ct = (r.headers.get('content-type') || '').toLowerCase();
      if (ct.startsWith('text/') || ct === 'application/json' || ct === 'application/xml') {
        toast.error('Legacy file is temporarily unavailable. Please retry in a moment.');
        return;
      }
      const cd = r.headers.get('content-disposition') || '';
      const m = cd.match(/filename\*=UTF-8''([^;]+)/i) || cd.match(/filename="?([^";]+)"?/i);
      const name = m ? decodeURIComponent(m[1]) : `${kind}-${order.id}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = name;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(`Downloaded ${name}`);
    } catch (e) {
      toast.error(`Legacy download failed: ${e?.message || e}`);
    } finally {
      setDownloading(null);
    }
  }, [order?.id, token]);

  const renderPath = (path, label, testid, fileType) => {
    if (!path) return null;
    const clickable = !!baseUrl;
    const href = clickable ? `${baseUrl}/${path.replace(/^\/+/, '')}` : null;
    return (
      <div className="text-xs flex items-baseline gap-2 flex-wrap" data-testid={testid}>
        <span className="text-slate-500 min-w-[110px]">{label}</span>
        {clickable ? (
          <a href={href} target="_blank" rel="noreferrer"
             className="font-mono text-[11px] text-emerald-700 hover:underline break-all">
            {path}↗
          </a>
        ) : (
          <span className="font-mono text-[11px] text-slate-700 break-all">{path}</span>
        )}
        {fileType && (
          <button
            type="button"
            onClick={() => downloadViaProxy(fileType)}
            disabled={downloading === fileType}
            data-testid={`${testid}-proxy-download-btn`}
            className="ml-1 text-[10px] px-1.5 py-0.5 rounded border border-cyan-300 text-cyan-700 bg-white hover:bg-cyan-50 disabled:opacity-50"
            title="Pull this file from fs.auto-remap.com via the backend proxy (keys stay backend-side)."
          >
            {downloading === fileType ? 'Downloading…' : 'Download via Legacy API'}
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="mt-3 p-3 rounded-lg border-2 border-violet-200 bg-violet-50"
         data-testid="order-legacy-block">
      <div className="flex items-center gap-1.5 mb-2">
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-violet-600 text-white">
          Legacy imported
        </span>
        <code className="text-[10px] text-slate-600 bg-white px-1 rounded">
          id {order.legacy_order_id || '—'}
        </code>
        {order.linked_transaction_id ? (
          <span className="text-[10px] text-emerald-700 bg-white px-1 rounded">
            linked txn {order.linked_transaction_id.slice(-8)}
          </span>
        ) : order.legacy_transaction_id ? (
          <span className="text-[10px] text-amber-700 bg-white px-1 rounded">
            legacy txn ref {order.legacy_transaction_id} (unlinked)
          </span>
        ) : null}
      </div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs mb-2">
        <div><span className="text-slate-500">Legacy status:</span> <code className="text-[10px] bg-white px-1 rounded">{order.legacy_status || '—'}</code></div>
        <div><span className="text-slate-500">Legacy user id:</span> <code className="text-[10px] bg-white px-1 rounded">{order.legacy_user_id ?? '—'}</code></div>
      </div>
      {!baseUrl && (order.legacy_file_default_path || order.legacy_file_modified_path) && (
        <p className="text-[10px] text-amber-700 mb-1.5 leading-snug" data-testid="legacy-files-warning">
          ⚠ Legacy file paths preserved, file storage not connected yet — set <code>legacy_file_base_url</code> in Legacy Migration → Settings, or use “Download via Legacy API” below (admin only).
        </p>
      )}
      {renderPath(order.legacy_file_default_path,  'Original file:', 'legacy-file-default',  'original')}
      {renderPath(order.legacy_file_modified_path, 'Modified file:', 'legacy-file-modified', 'modified')}
      {isAdmin && order.legacy_order_id && (
        <LegacyMappingDebug orderId={order.id} token={token} />
      )}
    </div>
  );
}

/**
 * Admin-only collapsible debug panel — shows the resolved mapping
 * that the backend will use on the next legacy download click.
 * Helps the operator spot when the OLD site has corrupted data
 * (e.g. a BMW file uploaded as the "original" slot of an Alfa Romeo
 * order). We never expose keys here.
 */
function LegacyMappingDebug({ orderId, token }) {
  const [open, setOpen] = React.useState(false);
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/orders/${encodeURIComponent(orderId)}/legacy-file-debug`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const j = await r.json().catch(() => ({}));
      setData(r.ok ? j : { error: j?.detail || `HTTP ${r.status}` });
    } catch (e) {
      setData({ error: e?.message || String(e) });
    } finally { setLoading(false); }
  }, [orderId, token]);
  React.useEffect(() => { if (open && !data) load(); }, [open, data, load]);
  return (
    <div className="mt-3 border-t border-violet-200 pt-2" data-testid="legacy-mapping-debug">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="legacy-mapping-debug-toggle"
        className="text-[10px] font-mono uppercase tracking-wider text-violet-700 hover:underline"
      >
        {open ? '▼' : '▶'} legacy mapping debug
      </button>
      {open && (
        <pre className="mt-1 text-[10px] bg-white border border-violet-200 rounded p-2 overflow-auto max-h-72 whitespace-pre-wrap break-all" data-testid="legacy-mapping-debug-content">
          {loading ? 'Loading…' : JSON.stringify(data, null, 2)}
        </pre>
      )}
    </div>
  );
}
